import React, { useState, useEffect } from "react";
import { FaPlus, FaCalendarAlt, FaTrash, FaBuilding, FaFilePdf, FaSpinner, FaEye, FaBullhorn, FaTimes } from "react-icons/fa";
import ScreenHeader from "../../components/ScreenHeader";
import { useBuilding } from "../../context/BuildingContext";
import { db, auth } from "../../firebase";
import { collection, query, where, getDocs, getDoc, addDoc, deleteDoc, updateDoc, setDoc, doc, serverTimestamp, runTransaction, writeBatch } from "firebase/firestore";
import { buildLiquidacionPdf } from "../../utils/liquidacionPdf";
import CierreGastosTabla from "../../components/CierreGastosTabla";
import { useToast } from "../../components/Toast";
import { useAppDialog } from "../../components/AppDialog";
import { isDemoUser, DEMO_BLOCK_MESSAGE } from "../../utils/demoMode";
import { collectUserIdsForBuilding, fetchUsersByUserIds, tolerantId } from "../../utils/firestoreHelpers";
import { consultaDelCierre } from "../../utils/consultasEdificio";
import { calculateInterestsForOverduePayments, getEffectivePaidDate, computeGraceEnd } from "../../utils/interestCalculation";
import { getOccupanciesOverlappingMonth } from "../../utils/occupancies";
import {
  legacyTenantHolder,
  legacyOwnerHolder,
  tenenciasPorUnidad,
  ventanaDeProracion,
  repartoGastoComun,
  repartoPropietario,
} from "../../utils/cierreResponsables";
import { roundMoney } from "../../utils/formatters";
import { roundPesos, pesosParaArriba } from "../../utils/redondeoPesos";
import { esUnidadDelEdificio, esAlquilerDelEdificio, esPagoDeAlquiler } from "../../utils/unidadesEdificio";
import { lineasDeAlquilerDelCierre, nombrePeriodo } from "../../utils/alquileresEdificio";
import { resolveCierreWindow, defaultMaturityFor, selectLiquidableExpenses, gastosYaLiquidados } from "../../utils/cierrePeriod";
import { isCommonExpense } from "../../utils/fundMovements";
import { computeCreditApplications } from "../../utils/creditApplication";
import { fundBreakdownKey, friendlyBreakdownName, getAllActiveFunds } from "../../utils/fundHelpers";
import { logAudit } from "../../utils/auditLog";
import { getRateForPreviousDay, getExchangeRate } from "../../utils/exchangeRates";
import { colors, shadows, radius, spacing, typography, transitions, btnPrimary, btnSecondary, btnGhost, inputBase, pageContainer, pageTitle, modalOverlay } from "../../utils/designTokens";
import { HABI_API_BASE_URL, getAuthHeaders } from "../../utils/habiApi";
import LoadingState from "../../components/LoadingState";

/**
 * Fichas `users` de los residentes del edificio para generar el cierre.
 *
 * El cierre las necesita para decidir de quién es la mora de un pago vencido.
 * Solo el sintético unit_<id> pasa su mora al ocupante actual de la unidad; un
 * deudor con userid real la conserva y, si su ficha no vuelve (con C10 no se
 * distingue "no existe" de "no se ve"), el resumen lo lista para revisarlo.
 * Con las fichas sin leer, todos los deudores quedarían sin verificar (antes,
 * peor: la mora de un ex ocupante, o la del propietario de una unidad
 * alquilada, se le cobraba al inquilino actual sin avisar). Por eso, si la
 * lectura falla, o no devuelve NINGUNA ficha habiendo residentes vinculados,
 * esto lanza y el cierre no se genera.
 *
 * Reglas 0D (C10): el staff lee las fichas por where("accessBids",
 * "array-contains", bid) (fetchUsersByUserIds); una ficha sin accessBids (sin
 * el backfill) no vuelve.
 *
 * @param {string | number} buildingId - Edificio del cierre
 * @returns {Promise<Array<Record<string, any>>>}
 */
async function cargarFichasDelCierre(buildingId) {
  let ids;
  let fichas;
  try {
    ids = await collectUserIdsForBuilding(buildingId);
    fichas = await fetchUsersByUserIds(ids, buildingId);
  } catch (e) {
    const detalle = e?.code || e?.message || String(e);
    throw new Error(
      `No se pudieron leer las fichas de los residentes del edificio (${detalle}). ` +
        "Sin ellas el cierre no puede asignar bien la mora de los pagos vencidos, así que no se generó. " +
        "Reintentá en unos minutos; si sigue pasando, avisá a soporte."
    );
  }
  if (ids.length > 0 && fichas.length === 0) {
    throw new Error(
      `El edificio tiene ${ids.length} residente(s) vinculado(s), pero no se pudo leer ninguna de sus fichas. ` +
        "Sin ellas el cierre no puede asignar bien la mora de los pagos vencidos, así que no se generó. " +
        "Avisá a soporte (fichas sin accessBids)."
    );
  }
  return fichas;
}

const CierreMes = ({ setSelected }) => {
  const { buildings, selectedBuilding, setSelectedBuilding } = useBuilding();
  const { showToast } = useToast();
  const { confirmDialog, alertDialog } = useAppDialog();
  
  // Usar el filtro global del contexto
  const buildingFilter = selectedBuilding?.buildingid || (buildings && buildings.length === 1 ? buildings[0].buildingid : null);
  
  const [cierres, setCierres] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showNewModal, setShowNewModal] = useState(false);
  const [saving, setSaving] = useState(false);
  const [interestPreview, setInterestPreview] = useState(null); // array of interest items for preview
  const [showInterestPreview, setShowInterestPreview] = useState(false);
  const [form, setForm] = useState({
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
    maturity: "",
    periodStart: "",
    periodEnd: "",
  });

  // A8 — Pre-validación de cierre
  const [preValidation, setPreValidation] = useState(null);
  const [preValidating, setPreValidating] = useState(false);
  const [forceClose, setForceClose] = useState(false); // override para criticals

  // A5 — Comunicado narrado del cierre
  const [narrativeModal, setNarrativeModal] = useState(null); // { month, year, title, body }
  const [narrativeLoading, setNarrativeLoading] = useState(false);
  const [publishingNovedad, setPublishingNovedad] = useState(false);

  const handleGenerateNarrative = async (cierre) => {
    if (!cierre || !buildingFilter) return;
    try {
      setNarrativeLoading(true);
      setNarrativeModal({ month: cierre.month, year: cierre.year, title: "", body: "", loading: true });
      const headers = await getAuthHeaders();
      const resp = await fetch(`${HABI_API_BASE_URL}/api/generate-closing-narrative`, {
        method: "POST",
        headers,
        body: JSON.stringify({
          building_id: String(buildingFilter),
          month: cierre.month,
          year: cierre.year,
        }),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        showToast(err.detail || "No se pudo generar el comunicado.", "error");
        setNarrativeModal(null);
        return;
      }
      const data = await resp.json();
      setNarrativeModal({
        month: cierre.month,
        year: cierre.year,
        title: data.title || `Cierre ${cierre.month}/${cierre.year}`,
        body: data.body_markdown || "",
      });
    } catch (e) {
      console.error("generate-closing-narrative error:", e);
      showToast(e.message || "Error inesperado al generar comunicado.", "error");
      setNarrativeModal(null);
    } finally {
      setNarrativeLoading(false);
    }
  };

  const handlePublishNarrativeAsNovedad = async () => {
    if (!narrativeModal || !narrativeModal.body || !buildingFilter) return;
    try {
      setPublishingNovedad(true);
      await addDoc(collection(db, "novedades"), {
        title: narrativeModal.title,
        description: narrativeModal.body,
        buildingid: Number(buildingFilter),
        type: "cierre_mensual",
        published: true,
        createdAt: serverTimestamp(),
        createdBy: auth.currentUser?.email || "Habitia",
        source: "closing_narrative_ai",
      });
      showToast("Comunicado publicado como novedad.", "success");
      setNarrativeModal(null);
    } catch (e) {
      console.error("publish novedad error:", e);
      showToast(e.message || "No se pudo publicar el comunicado.", "error");
    } finally {
      setPublishingNovedad(false);
    }
  };

  const runPreValidation = async () => {
    if (!buildingFilter || !form.month || !form.year) return;
    try {
      setPreValidating(true);
      setPreValidation(null);
      const headers = await getAuthHeaders();
      const url = `${HABI_API_BASE_URL}/api/closings/${encodeURIComponent(buildingFilter)}/${form.year}/${form.month}/validate`;
      const resp = await fetch(url, { method: "GET", headers });
      if (!resp.ok) {
        // La pre-validación es un pre-chequeo OPCIONAL que corre Habi. Si el
        // backend responde con error, no bloqueamos el cierre: lo marcamos como
        // "no disponible" (can_close: true) y el admin puede crear el cierre igual.
        const err = await resp.json().catch(() => ({}));
        console.warn("Pre-validación no disponible:", resp.status, err?.detail);
        setPreValidation({ unavailable: true, can_close: true, items: [], reason: err.detail || `HTTP ${resp.status}` });
        return;
      }
      const data = await resp.json();
      setPreValidation(data);
    } catch (e) {
      // Habi no accesible (sin backend en local, CORS o red caída). La
      // pre-validación es opcional: no mostramos un error alarmante ni
      // bloqueamos el cierre. Antes esto disparaba un toast "Failed to fetch".
      console.warn("Pre-validación no disponible:", e?.message || e);
      setPreValidation({ unavailable: true, can_close: true, items: [], reason: e?.message });
    } finally {
      setPreValidating(false);
    }
  };

  // Auto-validar cuando el modal de nuevo cierre se abre o cambia mes/año.
  useEffect(() => {
    if (showNewModal && buildingFilter) {
      runPreValidation();
    } else if (!showNewModal) {
      setPreValidation(null);
      setForceClose(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showNewModal, form.month, form.year, buildingFilter]);

  // Las fichas de los residentes no se cargan acá: el cierre las lee frescas al
  // generarse (cargarFichasDelCierre) y el PDF, las de sus pagos.
  useEffect(() => {
    if (buildingFilter) {
      fetchCierres();
    } else {
      setCierres([]);
    }
  }, [selectedBuilding, buildings]);

  // Auto-completar la VENTANA del cierre (según el día de corte del edificio) y la
  // fecha de vencimiento cuando cambia el mes/año/config. La ventana define el
  // período contable (ej. día de corte 5 → septiembre = 05/09 al 05/10); el
  // vencimiento (paymentDueDay) es independiente y cae en el mes del fin de ventana.
  // El admin puede editar ambas fechas en el modal (esto solo fija los defaults).
  useEffect(() => {
    if (!form.month || !form.year) return;
    const diaCorte = selectedBuilding?.diaCorte;
    const win = resolveCierreWindow(form.month, form.year, { diaCorte });
    const maturity = defaultMaturityFor(win, selectedBuilding?.paymentDueDay);
    setForm((prev) => ({
      ...prev,
      periodStart: win.start,
      periodEnd: win.end,
      maturity: maturity || prev.maturity,
    }));
  }, [selectedBuilding?.diaCorte, selectedBuilding?.paymentDueDay, form.month, form.year]);

  const fetchCierres = async () => {
    setLoading(true);
    try {
      const q = query(
        collection(db, "montlyaccount"),
        where("buildingid", "in", tolerantId(buildingFilter))
      );
      const snap = await getDocs(q);
      
      // Si no hay documentos, devolver array vacío
      if (snap.empty) {
        setCierres([]);
        return;
      }
      
      const raw = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      // Deduplicar por id (previene duplicate key warnings)
      const seen = new Set();
      const data = raw.filter(c => {
        if (seen.has(c.id)) return false;
        seen.add(c.id);
        return true;
      });
      // Ordenar por año y mes descendente
      data.sort((a, b) => {
        if (b.year !== a.year) return b.year - a.year;
        return b.month - a.month;
      });
      setCierres(data);
    } catch (error) {
      console.error("Error cargando cierres:", error);
      // Si la colección no existe o hay error, simplemente mostrar lista vacía
      setCierres([]);
    } finally {
      setLoading(false);
    }
  };


  const getMonthName = (month) => {
    const months = [
      "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
      "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
    ];
    return months[month - 1] || "";
  };

  // "YYYY-MM-DD" → "DD/MM/YYYY" (para el preview del período).
  const fmtISO = (iso) => (iso && String(iso).length >= 10 ? String(iso).slice(0, 10).split("-").reverse().join("/") : "—");

  // Form inicial del modal con la ventana (día de corte) ya resuelta para el mes actual.
  const freshForm = () => {
    const rm = new Date().getMonth() + 1;
    const ry = new Date().getFullYear();
    const w = resolveCierreWindow(rm, ry, { diaCorte: selectedBuilding?.diaCorte });
    return {
      month: rm,
      year: ry,
      periodStart: w.start,
      periodEnd: w.end,
      maturity: defaultMaturityFor(w, selectedBuilding?.paymentDueDay),
    };
  };

  // Validación de la ventana del período (fin > inicio + contigüidad con el cierre
  // anterior). No bloquea el cierre salvo end<=start; el resto son avisos.
  const getPeriodValidation = () => {
    const { periodStart: s, periodEnd: e, month, year } = form;
    if (!s || !e) return { errors: [], warnings: [], prevEnd: null };
    const errors = [];
    const warnings = [];
    if (e <= s) errors.push("La fecha 'hasta' debe ser posterior a la fecha 'desde'.");
    // Cierre del mes anterior (misma etiqueta -1).
    const pm = month === 1 ? 12 : month - 1;
    const py = month === 1 ? year - 1 : year;
    const prev = (cierres || []).find(
      (c) => Number(c.month) === pm && Number(c.year) === py && c.periodEnd
    );
    const prevEnd = prev ? String(prev.periodEnd).slice(0, 10) : null;
    if (prevEnd && prevEnd !== s) {
      warnings.push(
        `La ventana no arranca donde terminó el cierre de ${getMonthName(pm)} ${py} (${fmtISO(prevEnd)}). ` +
        (prevEnd < s ? "Puede quedar un hueco de días sin liquidar." : "Puede solaparse con el período anterior.")
      );
    }
    return { errors, warnings, prevEnd };
  };

  const formatDate = (date) => {
    if (!date) return "—";
    try {
      const dateObj = date.toDate ? date.toDate() : new Date(date);
      return dateObj.toLocaleDateString("es-UY", {
        day: "2-digit",
        month: "2-digit",
        year: "numeric",
      });
    } catch (error) {
      return "—";
    }
  };

  const handleGenerarPDF = async (cierre) => {
    try {
      await buildLiquidacionPdf({
        buildingId: buildingFilter,
        cierre,
        selectedBuilding,
        output: "download",
      });
      // Si este toast aparece pero el archivo no baja, el problema es el
      // NAVEGADOR bloqueando la descarga (icono de descarga tachado en la
      // barra de direcciones), no la generación.
      showToast("PDF generado. Si no se descargó, revisá el bloqueo de descargas del navegador.", "success");
    } catch (error) {
      console.error("Error generando PDF:", error);
      showToast("Error al generar el PDF: " + error.message, "error");
    }
  };

  const handleDeleteCierre = async (cierreId) => {
    if (await isDemoUser()) return showToast(DEMO_BLOCK_MESSAGE, "warning");
    // Obtener los datos del cierre antes de eliminarlo
    const cierreDoc = await getDoc(doc(db, "montlyaccount", cierreId));
    if (!cierreDoc.exists()) {
      showToast("El cierre no existe.", "error");
      return;
    }
    
    const cierreData = cierreDoc.data();
    const month = cierreData.month;
    const year = cierreData.year;
    const buildingid = cierreData.buildingid;
    
    const okDel = await confirmDialog(
      `¿Eliminar este cierre de mes?\n\nMes: ${getMonthName(month)} ${year}\n\nEsto eliminará:\n- El registro del cierre\n- Todos los expensesapartments de este mes/año\n- Todos los payments generados para este mes/año`,
      { title: "Eliminar cierre de mes", danger: true, confirmLabel: "Eliminar" }
    );
    if (!okDel) return;

    try {
      const BATCH_LIMIT = 450;

      // 1. Recopilar todos los documentos a eliminar/revertir. Las consultas por
      // montlyaccountId fijan el edificio del cierre (reglas 0D): pasan también
      // con ids de cierre viejos (aleatorios) y un doc de otro edificio que
      // apunte a este cierre no entra al batch ni lo traba.
      const cierreBid = buildingid ?? buildingFilter;
      const eaByMa = consultaDelCierre(db, "expensesapartments", cierreId, cierreBid);
      const eaByMaSnap = await getDocs(eaByMa);
      let expensesApartmentsToDelete = eaByMaSnap.docs;
      if (expensesApartmentsToDelete.length === 0) {
        const eaLegacy = query(collection(db, "expensesapartments"), where("buildingid", "in", tolerantId(buildingid)), where("month", "==", month), where("year", "==", year));
        expensesApartmentsToDelete = (await getDocs(eaLegacy)).docs;
      }

      const monthName = getMonthName(month);
      const expectedDescription = `cierre de mes ${monthName}`;
      const payByMa = consultaDelCierre(db, "payments", cierreId, cierreBid);
      let paymentsToDelete = (await getDocs(payByMa)).docs;
      if (paymentsToDelete.length === 0) {
        const payLegacy = query(collection(db, "payments"), where("buildingid", "in", tolerantId(buildingid)), where("description", "==", expectedDescription), where("month", "==", month), where("year", "==", year));
        paymentsToDelete = (await getDocs(payLegacy)).docs;
      }
      if (paymentsToDelete.length === 0) {
        const payFallback = query(collection(db, "payments"), where("buildingid", "in", tolerantId(buildingid)), where("description", "==", expectedDescription));
        paymentsToDelete = (await getDocs(payFallback)).docs;
      }

      const multasQ = query(collection(db, "multas"), where("buildingid", "in", tolerantId(buildingid)), where("includedInCierreId", "==", cierreId));
      const multasToRevert = (await getDocs(multasQ)).docs;

      const chargesQ = query(collection(db, "apartmentcharges"), where("buildingid", "in", tolerantId(buildingid)), where("includedInCierreId", "==", cierreId));
      const chargesToRevert = (await getDocs(chargesQ)).docs;

      // FIN-029: Revertir amenity charges con status "included_in_cierre" o "locked_for_cierre"
      const amenityQ1 = query(collection(db, "amenitycharges"), where("buildingid", "in", tolerantId(buildingid)), where("cierreMonth", "==", month), where("cierreYear", "==", year), where("status", "==", "included_in_cierre"));
      const amenityQ2 = query(collection(db, "amenitycharges"), where("buildingid", "in", tolerantId(buildingid)), where("lockedByCierreId", "==", cierreId), where("status", "==", "locked_for_cierre"));
      const [amenitySnap1, amenitySnap2] = await Promise.all([getDocs(amenityQ1), getDocs(amenityQ2)]);
      const amenityRevertMap = new Map();
      amenitySnap1.docs.forEach(d => amenityRevertMap.set(d.id, d));
      amenitySnap2.docs.forEach(d => amenityRevertMap.set(d.id, d));
      const amenityToRevert = Array.from(amenityRevertMap.values());

      // 2. Ejecutar todas las operaciones en batches atomicos (FIN-010)
      const allOps = [];
      expensesApartmentsToDelete.forEach(d => allOps.push({ type: "delete", ref: d.ref }));
      paymentsToDelete.forEach(d => allOps.push({ type: "delete", ref: d.ref }));
      multasToRevert.forEach(d => allOps.push({ type: "update", ref: d.ref, data: { status: "activa", includedInCierreId: null } }));
      chargesToRevert.forEach(d => allOps.push({ type: "update", ref: d.ref, data: { status: "activa", includedInCierreId: null } }));
      amenityToRevert.forEach(d => allOps.push({ type: "update", ref: d.ref, data: { status: "pending", cierreMonth: null, cierreYear: null, lockedAt: null, lockedByCierreId: null } }));
      allOps.push({ type: "delete", ref: doc(db, "montlyaccount", cierreId) });

      for (let i = 0; i < allOps.length; i += BATCH_LIMIT) {
        const chunk = allOps.slice(i, i + BATCH_LIMIT);
        const batch = writeBatch(db);
        chunk.forEach(op => {
          if (op.type === "delete") batch.delete(op.ref);
          else if (op.type === "update") batch.update(op.ref, op.data);
        });
        await batch.commit();
      }

      const deletedExpensesApartments = expensesApartmentsToDelete.length;
      const deletedPayments = paymentsToDelete.length;
      const revertedMultas = multasToRevert.length;
      const revertedCargos = chargesToRevert.length;
      const revertedCharges = amenityToRevert.length;

      await logAudit("delete_cierre", { buildingId: buildingid, entityId: cierreId, entityType: "montlyaccount", previousValue: { month, year, deletedExpensesApartments, deletedPayments, revertedMultas, revertedCargos, revertedCharges } });
      await alertDialog(
        `Cierre eliminado exitosamente.\n\nEliminados:\n- ${deletedExpensesApartments} registros en expensesapartments\n- ${deletedPayments} pagos en payments${revertedMultas > 0 ? `\n- ${revertedMultas} multas revertidas a activa` : ""}${revertedCargos > 0 ? `\n- ${revertedCargos} cargos puntuales revertidos a activa` : ""}${revertedCharges > 0 ? `\n- ${revertedCharges} cargos de amenities revertidos` : ""}`,
        { title: "Listo", variant: "success" }
      );
      fetchCierres();
    } catch (error) {
      console.error("Error eliminando cierre:", error);
      showToast("Error al eliminar el cierre: " + error.message, "error");
    }
  };

  const handleCreateCierre = async (approvedInterestItems = null) => {
    if (!form.maturity || !buildingFilter) {
      showToast("Completá todos los campos requeridos", "warning");
      return;
    }
    if (await isDemoUser()) return showToast(DEMO_BLOCK_MESSAGE, "warning");

    // Verificar si hay amenity charges del mes sin residente asignado (byUnit)
    {
      const byUnitQ = query(
        collection(db, "amenitycharges"),
        where("buildingid", "in", tolerantId(buildingFilter)),
        where("month", "==", form.month),
        where("year", "==", form.year),
        where("status", "==", "pending"),
        where("byUnit", "==", true)
      );
      const byUnitSnap = await getDocs(byUnitQ);
      if (byUnitSnap.size > 0) {
        const lista = byUnitSnap.docs.map((d) => {
          const c = d.data();
          return `• ${c.amenity || "Amenity"} — ${c.date || ""} — Turno ${c.turno || "-"} (Unidad ${c.apartmentId || "-"})`;
        }).join("\n");
        const accion = await confirmDialog(
          `Hay ${byUnitSnap.size} cargo(s) de amenities asociados a una unidad sin propietario/inquilino asignado:\n\n${lista}\n\n¿Qué querés hacer?`,
          {
            title: "Cargos de amenities sin residente",
            confirmLabel: "Incluir igual y continuar",
            cancelLabel: "No incluir en el cierre",
            wide: true,
          }
        );
        // accion === true → incluir igual; false → excluir del cierre
        if (accion === false) {
          // Marcar como excluidos para que no entren en este cierre
          const batchExclude = writeBatch(db);
          byUnitSnap.docs.forEach((d) => {
            batchExclude.update(d.ref, { status: "excluded_from_cierre", excludedAt: serverTimestamp() });
          });
          await batchExclude.commit();
        }
        // Si accion === null (cerró sin elegir) → cancelar el cierre
        if (accion === null) return;
      }
    }

    setSaving(true);

    // Fichas de los residentes, frescas y ANTES del candado y de cualquier
    // escritura: si no se pueden leer, el cierre no se genera (nunca se sigue
    // con la lista vacía, que reasignaría la mora sin avisar).
    let fichasCierre;
    try {
      fichasCierre = await cargarFichasDelCierre(buildingFilter);
    } catch (fichasErr) {
      console.error("Cierre: no se pudieron leer las fichas de los residentes:", fichasErr);
      await alertDialog(fichasErr.message, { title: "No se generó el cierre", variant: "warning" });
      setSaving(false);
      return;
    }

    let state = "ok";
    let errorMessage = "";
    const cierreDocId = `${Number(buildingFilter)}_${form.year}_${form.month}`;
    const cierreDocRef = doc(db, "montlyaccount", cierreDocId);

    // Ventana del período (fecha de corte configurable). Se persiste en el cierre
    // y en cada doc generado para que las vistas de período y la conciliación
    // bucketeen por la ventana. Ver cierrePeriod.js / resolveCierreWindow.
    const cierrePeriodStart = form.periodStart
      || resolveCierreWindow(form.month, form.year, { diaCorte: selectedBuilding?.diaCorte }).start;
    const cierrePeriodEnd = form.periodEnd
      || resolveCierreWindow(form.month, form.year, { diaCorte: selectedBuilding?.diaCorte }).end;

    try {
      // Idempotencia + lock transaccional por edificio/mes/año
      await runTransaction(db, async (tx) => {
        const lockSnap = await tx.get(cierreDocRef);
        const existingState = lockSnap.data()?.state;
        if (lockSnap.exists() && existingState !== "error" && existingState !== "processing") {
          throw new Error("Este cierre ya fue ejecutado para el período seleccionado.");
        }
        tx.set(cierreDocRef, {
          month: form.month,
          year: form.year,
          buildingid: Number(buildingFilter),
          state: "processing",
          createdAt: serverTimestamp(),
          maturity: form.maturity,
          periodStart: cierrePeriodStart,
          periodEnd: cierrePeriodEnd,
          createdByEmail: auth.currentUser?.email || null,
          createdByUid: auth.currentUser?.uid || null,
          approvalStatus: "pending",
          // En un reintento (state error/processing) este set pisa el doc entero
          // y el validador automático NO re-corre (onCierreCreated dispara solo
          // en el create). Conservamos su resultado del intento anterior.
          ...(lockSnap.exists() && lockSnap.data().autoValidation
            ? { autoValidation: lockSnap.data().autoValidation }
            : {}),
        });
      });

      // 1. Gastos a liquidar: TODOS los gastos PAGADOS que todavía no entraron
      // en ningún cierre, pagados hasta la fecha de este cierre.
      //
      // Antes se filtraba por paidDate dentro del MES del cierre, y eso dejaba
      // dos puntos ciegos: (a) el modelo típico "pago las facturas en julio y
      // las liquido en el cierre de agosto" encontraba 0 gastos; (b) un gasto
      // pagado después de ejecutar el cierre del mes no lo tomaba NINGÚN cierre
      // (se perdía), y uno pagado antes podía duplicarse entre cierres.
      //
      // "Ya liquidado" se deriva de expensesapartments.expenseId (cada cierre
      // guarda ahí qué gasto aplicó, y eliminar un cierre borra sus registros,
      // liberando el gasto para el próximo cierre — sin estado huérfano).
      // Ventana del cierre (fecha de corte configurable). El tope de selección de
      // gastos es el FIN de la ventana (periodEnd), NO el vencimiento ni el fin de
      // mes calendario. periodStart queda implícito: el gate relacional
      // (expensesapartments.expenseId de otros cierres) impide el doble conteo.
      // windowBounds es la ventana de la PRORACIÓN ((start, end]): sin día de
      // corte arranca el día anterior para que el día 1 cuente.
      const windowBounds = ventanaDeProracion({
        month: form.month,
        year: form.year,
        periodStart: cierrePeriodStart,
        periodEnd: cierrePeriodEnd,
        diaCorte: selectedBuilding?.diaCorte,
      });
      const endDate = new Date(String(cierrePeriodEnd).slice(0, 10) + 'T23:59:59');


      const expensesQuery = query(
        collection(db, "expenses"),
        where("buildingid", "in", tolerantId(buildingFilter))
      );
      const expensesSnap = await getDocs(expensesQuery);
      const allExpenses = expensesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));


      // Backfill: gastos pagados sin paidDate reciben la fecha de hoy
      const today = new Date().toISOString().split('T')[0];
      for (const exp of allExpenses) {
        if (exp.paid && !exp.paidDate) {
          exp.paidDate = today;
          try { await updateDoc(doc(db, "expenses", exp.id), { paidDate: today }); } catch (e) { /* ignorar */ }
        }
      }

      // Limpieza de re-ejecución: el flujo es de DOS fases cuando hay mora
      // (fase 1 escribe expensesapartments, pausa para el preview de intereses
      // con un return, y al aprobar la función se re-ejecuta entera). Los restos
      // de la fase 1 de ESTE MISMO cierre hay que borrarlos antes de recalcular
      // — si no, quedan duplicados y además el pool de "ya liquidados" contaba
      // los gastos del propio cierre en curso y el GC salía en $0.
      const BATCH_LIMIT_DELETE = 450;
      for (const colName of ["expensesapartments", "payments"]) {
        const leftoverSnap = await getDocs(
          consultaDelCierre(db, colName, cierreDocId, buildingFilter)
        );
        if (!leftoverSnap.empty) {
          for (let i = 0; i < leftoverSnap.docs.length; i += BATCH_LIMIT_DELETE) {
            const chunk = leftoverSnap.docs.slice(i, i + BATCH_LIMIT_DELETE);
            const delBatch = writeBatch(db);
            chunk.forEach((d) => delBatch.delete(d.ref));
            await delBatch.commit();
          }
        }
      }

      // Gastos ya liquidados por OTROS cierres de este edificio. Se excluyen:
      //  - el propio cierre en curso (sus filas son de una fase anterior), y
      //  - cierres INCOMPLETOS (state processing/error abandonados): sus filas
      //    no cobraron a nadie, no deben sacar gastos del pool.
      // Filas de cierres borrados que dejaron artefactos (fantasmas) SÍ cuentan:
      // sus payments pudieron cobrarse — mejor no re-liquidar (lado seguro).
      // (Lógica pura en cierrePeriod.gastosYaLiquidados, la misma que usa el informe.)
      const maBuildingSnap = await getDocs(
        query(collection(db, "montlyaccount"), where("buildingid", "in", tolerantId(buildingFilter)))
      );
      const appliedSnap = await getDocs(
        query(collection(db, "expensesapartments"), where("buildingid", "in", tolerantId(buildingFilter)))
      );
      const appliedExpenseIds = gastosYaLiquidados({
        cierres: maBuildingSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
        filasAplicadas: appliedSnap.docs.map((d) => d.data() || {}),
        excluirCierreId: cierreDocId,
      });

      // Filtrar: pagados, sin liquidar, con fecha de pago hasta el cierre.
      // Selección de gastos: pagados, no liquidados por otro cierre (anti
      // doble-conteo vía appliedExpenseIds) y con paidDate <= fin de ventana.
      // Lógica pura en cierrePeriod.selectLiquidableExpenses (testeable).
      const expenses = selectLiquidableExpenses(allExpenses, { appliedExpenseIds, endDate });

      
      // Mostrar información de los expenses encontrados
      const expensesInfo = expenses.map(exp => {
        const dateField = exp.paidDate;
        let dateStr = "N/A";
        try {
          if (typeof dateField === 'string') {
            dateStr = dateField;
          } else if (dateField?.toDate) {
            dateStr = dateField.toDate().toISOString().split('T')[0];
          } else if (dateField?.seconds) {
            dateStr = new Date(dateField.seconds * 1000).toISOString().split('T')[0];
          }
        } catch (e) {
          dateStr = String(dateField);
        }
        return `- ${exp.description || exp.id}: ${dateStr} ($${exp.amount || 0})`;
      }).join('\n');

      const paidCount = allExpenses.filter(e => e.paid).length;
      if (expenses.length === 0) {
        await alertDialog(
          `No hay gastos pagados pendientes de liquidar (hasta ${form.maturity || `${form.month}/${form.year}`}).\n\nTotal de gastos del edificio: ${allExpenses.length} (${paidCount} pagados, ${appliedExpenseIds.size} ya liquidados en cierres anteriores)\n\nVerificá que los gastos estén marcados como pagados con su fecha de pago.`,
          { title: "Sin gastos para liquidar", variant: "warning" }
        );
        // Limpiar el documento de cierre creado en la transacción
        try { await deleteDoc(cierreDocRef); } catch (e) { /* ignorar */ }
        setSaving(false);
        return;
      }

      // Confirmación con el detalle en TABLA. Solo se listan los gastos que se
      // REPARTEN entre las unidades (caja); los que paga un fondo (reserva o
      // fondo propio) también entran al cierre —para la contabilidad del fondo y
      // el PDF— pero no se cobran a las unidades, así que van en una sola línea
      // aclaratoria y no en la tabla (antes parecía que se iban a repartir).
      const gastosCaja = expenses.filter((e) => isCommonExpense(e));
      const gastosFondo = expenses.filter((e) => !isCommonExpense(e));
      const totalCaja = gastosCaja.reduce((s, e) => s + (Number(e.amount) || 0), 0);
      const totalFondo = gastosFondo.reduce((s, e) => s + (Number(e.amount) || 0), 0);
      const notaFondos = gastosFondo.length
        ? `Además, ${gastosFondo.length} gasto(s) por $${totalFondo.toLocaleString("es-UY")} los paga un fondo (reserva u otro): no se reparten entre las unidades.`
        : "";
      // En la fase 2 (post-aprobación de mora) no se vuelve a preguntar: los
      // gastos ya se confirmaron en la fase 1 y el recálculo es idéntico.
      if (!Array.isArray(approvedInterestItems)) {
        const okLiquidar = await confirmDialog(
          `Se van a repartir ${gastosCaja.length} gasto(s) entre las unidades por $${totalCaja.toLocaleString("es-UY")}.${notaFondos ? `\n\n${notaFondos}` : ""}\n\n¿Crear el cierre de ${getMonthName(form.month)} ${form.year}?`,
          {
            title: "Confirmar gastos del cierre",
            confirmLabel: "Crear cierre",
            wide: true,
            content: gastosCaja.length ? <CierreGastosTabla rows={gastosCaja} /> : null,
          }
        );
        if (!okLiquidar) {
          try { await deleteDoc(cierreDocRef); } catch (e) { /* ignorar */ }
          setSaving(false);
          return;
        }
      }

      // Lista EXACTA de gastos que liquida este cierre, incluidos los que paga un
      // fondo (esos no generan expensesapartments). La usan: los cierres
      // siguientes para no volver a tomarlos (appliedExpenseIds) y el PDF para el
      // "Detalle de Gastos del Mes" (antes adivinaba por fecha de factura y se
      // perdía, p.ej., un certificado con factura de 2024 pagado este mes).
      try {
        await updateDoc(cierreDocRef, { liquidatedExpenseIds: expenses.map((e) => e.id) });
      } catch (e) {
        console.warn("No se pudo guardar liquidatedExpenseIds en el cierre:", e.message);
      }
      
      // Mostrar información de todos los expenses del edificio para debug
      const allExpensesInfo = allExpenses.map(exp => {
        let invoiceDateStr = "SIN FECHA";
        try {
          if (exp.invoiceDate) {
            if (typeof exp.invoiceDate === 'string') {
              invoiceDateStr = exp.invoiceDate;
            } else if (exp.invoiceDate?.toDate) {
              invoiceDateStr = exp.invoiceDate.toDate().toISOString().split('T')[0];
            } else if (exp.invoiceDate?.seconds) {
              invoiceDateStr = new Date(exp.invoiceDate.seconds * 1000).toISOString().split('T')[0];
            } else {
              invoiceDateStr = String(exp.invoiceDate);
            }
          }
        } catch (e) {
          invoiceDateStr = "ERROR: " + String(exp.invoiceDate);
        }
        return `- ${exp.description || exp.id}: ${invoiceDateStr} ($${exp.amount || 0})`;
      }).slice(0, 20).join('\n'); // Limitar a 20 para no hacer el popup muy largo
      

      // 2. Obtener edificio para m2 total
      const buildingSnap = await getDocs(
        query(collection(db, "building"), where("buildingid", "in", tolerantId(buildingFilter)))
      );
      const building = buildingSnap.docs[0]?.data();
      // Convertir m2 a número (puede venir como string "100" o número 100)
      const buildingM2 = Number(building?.m2) || 1;

      // 3. Obtener todos los apartamentos del edificio
      const apartmentsQuery = query(
        collection(db, "apartments"),
        where("building", "in", tolerantId(buildingFilter))
      );
      const apartmentsSnap = await getDocs(apartmentsQuery);
      // Las unidades del edificio (portería y "Alquiler del edificio") NO participan
      // del reparto de gastos comunes ni de los fondos: se excluyen del universo de
      // unidades del cierre. El alquiler de una unidad "Alquiler del edificio" lo
      // cobra este cierre aparte (contrato en `alquileres`, ver más abajo).
      const apartmentsAll = apartmentsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const apartments = apartmentsAll.filter(a => !esUnidadDelEdificio(a));

      // 3.5. Tenencias (occupancies) que solapan el mes del cierre, indexadas por
      // apartmentId y rol. Base de la PRORACIÓN por cambio de ocupante a mitad de
      // mes. Las unidades SIN transición (0 o 1 tramo por rol) se comportan
      // exactamente igual que antes (todo al ocupante vigente).
      let monthOccupancies = [];
      try {
        monthOccupancies = await getOccupanciesOverlappingMonth(buildingFilter, form.month, form.year, windowBounds);
      } catch (occErr) {
        console.error("Error cargando occupancies del mes (se usa modo legacy):", occErr);
        monthOccupancies = [];
      }
      // aptId -> { tenant: [tramos], owner: [tramos] }, solo del lugar titular
      // (el complementario no responde por GC ni fondos).
      const occByApt = tenenciasPorUnidad(monthOccupancies);

      // 3.6. Alquiler de las unidades del edificio ("Alquiler del edificio"): el
      // contrato de cada unidad (`alquileres/{aptId}`) dice cuánto se cobra y a qué
      // fondo; este cierre le cobra al inquilino el alquiler del período que toca
      // (adelantado: el mes siguiente; vencido: este mes). Nunca genera mora y lo
      // cobrado entra como ingreso del fondo (fundBalances.ingresosDeAlquiler).
      let lineasAlquiler = [];
      if (apartmentsAll.some(esAlquilerDelEdificio)) {
        let avisosAlquiler = [];
        try {
          const [contratosSnap, pagosSnap, ingresosSnap] = await Promise.all([
            getDocs(query(collection(db, "alquileres"), where("buildingid", "in", tolerantId(buildingFilter)))),
            getDocs(query(collection(db, "payments"), where("buildingid", "in", tolerantId(buildingFilter)))),
            getDocs(query(collection(db, "buildingincomes"), where("buildingid", "in", tolerantId(buildingFilter)))),
          ]);
          const r = lineasDeAlquilerDelCierre({
            contratos: contratosSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
            apartmentsAll,
            occByApt,
            month: form.month,
            year: form.year,
            cierreId: cierreDocId,
            pagosPrevios: pagosSnap.docs.map((d) => ({ id: d.id, ...d.data() })),
            ingresosManuales: ingresosSnap.docs.map((d) => d.data()),
            fondosActivos: getAllActiveFunds(building).map((f) => f.id),
          });
          lineasAlquiler = r.lineas;
          avisosAlquiler = r.avisos;
        } catch (alqErr) {
          console.error("Error leyendo los contratos de alquiler:", alqErr);
          avisosAlquiler = [`No se pudieron leer los contratos de alquiler (${alqErr.code || alqErr.message}): este cierre no va a cobrar alquileres.`];
        }
        // Avisos solo en la fase 1 (la fase 2, tras aprobar la mora, recalcula lo mismo).
        if (avisosAlquiler.length && !Array.isArray(approvedInterestItems)) {
          const seguir = await confirmDialog(
            `${avisosAlquiler.join("\n\n")}\n\n¿Seguir con el cierre?`,
            { title: "Alquileres de unidades del edificio", confirmLabel: "Seguir", wide: true }
          );
          if (!seguir) {
            try { await deleteDoc(cierreDocRef); } catch (e) { /* ignorar */ }
            setSaving(false);
            return;
          }
        }
      }
      // Detalle de proración por unidad, para marcar los payments resultantes.
      const prorationByApt = {}; // aptId -> [{ userid, role, days, amount, concept }]
      const recordProration = (apt, concept, share) => {
        const k = String(apt.id);
        if (!prorationByApt[k]) prorationByApt[k] = [];
        prorationByApt[k].push({
          userid: String(share.userid),
          role: share.role,
          days: share.days,
          amount: share.amount,
          concept,
        });
      };

      // 4. Obtener criterios del edificio desde criteriabuilding Y criterios globales de criteriaexpenses
      // Los gastos pueden tener criterios de cualquiera de las dos colecciones
      
      const criteriaMap = {};
      
      // Primero cargar criterios específicos del edificio (criteriabuilding)
      const criteriaBuildingQuery = query(
        collection(db, "criteriabuilding"),
        where("buildingid", "in", tolerantId(buildingFilter))
      );
      const criteriaBuildingSnap = await getDocs(criteriaBuildingQuery);
      criteriaBuildingSnap.docs.forEach(doc => {
        const data = doc.data();
        criteriaMap[doc.id] = data;
      });
      
      // También cargar criterios globales de criteriaexpenses (por si el gasto tiene un criterio de ahí)
      const criteriaExpensesQuery = query(collection(db, "criteriaexpenses"));
      const criteriaExpensesSnap = await getDocs(criteriaExpensesQuery);
      criteriaExpensesSnap.docs.forEach(doc => {
        const data = doc.data();
        // Solo agregar si no existe ya (evitar sobrescribir criterios del edificio)
        if (!criteriaMap[doc.id]) {
          criteriaMap[doc.id] = data;
        }
      });
      

      // 4.5. Determinar método de distribución del edificio y validar
      const buildingDistMethod = building?.distributionMethod || "alicuotas";

      if (buildingDistMethod === "alicuotas") {
        const aptsWithoutAlicuota = apartments.filter(a => !Number(a.alicuota) || Number(a.alicuota) <= 0);
        if (aptsWithoutAlicuota.length > 0) {
          const unidadesSin = aptsWithoutAlicuota.map(a => a.number || a.id).join(", ");
          throw new Error(`No se puede cerrar el mes. Las siguientes unidades no tienen alícuota configurada: ${unidadesSin}. Configure las alícuotas en Editar Edificio.`);
        }
        // La suma 1000‰ se valida sobre TODAS las unidades (incluida la portería):
        // el reglamento define las milésimas del edificio completo. La portería se
        // excluye del REPARTO (su parte se redistribuye entre las demás vía totalWeight),
        // pero su alícuota sigue siendo parte del 1000‰ del reglamento.
        const sumaAlicuotas = apartmentsAll.reduce((sum, a) => sum + (Number(a.alicuota) || 0), 0);
        if (Math.abs(sumaAlicuotas - 1000) > 1) {
          throw new Error(`La suma de alícuotas es ${sumaAlicuotas.toFixed(2)}‰. Debe ser 1000‰. Ajuste en Editar Edificio.`);
        }
      } else if (buildingDistMethod === "superficie") {
        const aptsSinM2 = apartments.filter(a => !Number(a.m2) || Number(a.m2) <= 0);
        if (aptsSinM2.length > 0) {
          const unidadesSin = aptsSinM2.map(a => a.number || a.id).join(", ");
          throw new Error(`No se puede cerrar el mes. Las siguientes unidades no tienen m² configurados: ${unidadesSin}. Configure los m² en Editar Edificio.`);
        }
      }

      // 5. Procesar cada gasto
      const expensesApartments = [];
      // Gastos sin criterio (o con criterio inválido): se reparten con el método
      // del edificio. Antes había un aviso por gasto (muchos clics); ahora se
      // informa en UNA línea del resumen final.
      const gastosSinCriterio = [];

      for (const expense of expenses) {
        // Lo que paga un fondo (obras, o incidencias/obras con sourceFund
        // "reserva" o fondo propio) se descuenta de ese fondo: NO se reparte.
        if (!isCommonExpense(expense)) continue;

        // Obtener el ID del criterio desde el gasto
        const criteriaId = expense.criteria;
        
        // Buscar el criterio en el mapa usando el ID
        // expense.criteria contiene el ID del documento de CriteriaExpenses o Criteriabuilding
        const criteria = criteriaId ? criteriaMap[criteriaId] : null;
        
        // Debug: mostrar información del criterio encontrado
        if (!criteria && criteriaId) {
          console.warn(`Cierre: criterio "${criteriaId}" del gasto "${expense.description || expense.id}" no encontrado. Disponibles: ${Object.keys(criteriaMap).join(", ")}`);
        }
        
        // Obtener el field del criterio (viene en minúsculas: "all", "tenant", "owner", "garage", etc.)
        let field = (criteria?.field || "").toLowerCase();
        
        // Debug: mostrar el field encontrado
        if (criteria && !field) {
          console.warn(`Cierre: el criterio "${criteriaId}" no tiene campo "field":`, criteria);
        }
        
        // Normalizar para comparación con los valores especiales
        let normalizedField = field;
        if (field === "tenant" || field === "inquilino") {
          normalizedField = "tenant";
        } else if (field === "owner" || field === "propietario") {
          normalizedField = "owner";
        } else if (field === "all") {
          normalizedField = "all";
        }
        // Si no es ninguno de estos, field será un campo específico de apartments (ej: "garage", "balcony")
        
        // Determinar método de distribución: override del gasto > legacy dividePorSuperficie > método del edificio
        const distMethod = expense.overrideDistribution
          || (expense.dividePorSuperficie ? "superficie" : null)
          || buildingDistMethod;
        let expenseAmount = Number(expense.amount) || 0;

        if (expenseAmount === 0) continue;

        // F7: gastos comunes en USD → convertir a UYU al TC del BCU ANTES de repartir.
        // Sin esto se repartían a valor nominal (US$100 = $100), sub-cobrando al residente.
        // Mismo helper que amenity/multa/cargo; si no hay TC del BCU se usa el manual del
        // edificio; si tampoco hay, NO se reparte ese gasto (no repartir mal) y se avisa.
        if ((expense.money || "peso").toLowerCase() === "dolar") {
          let sell = 0;
          try {
            let rate = await getRateForPreviousDay(expense.invoiceDate || expense.date || expense.createdAt);
            if (!rate) {
              const { getLatestRate } = await import("../../utils/exchangeRates");
              rate = await getLatestRate();
            }
            sell = Number(rate?.sell) || 0;
          } catch (fxErr) {
            console.error("Cierre: error obteniendo TC para gasto USD:", fxErr);
          }
          if (sell <= 0) sell = Number(building?.exchangeRateUSD) || 0; // fallback manual del edificio
          if (sell > 0) {
            expenseAmount = roundMoney(expenseAmount * sell);
          } else {
            console.error(`Cierre: gasto USD "${expense.description || expense.id}" sin tipo de cambio; se OMITE del reparto.`);
            showToast(`Sin tipo de cambio para "${expense.description || expense.id}" (USD): se omitió del cierre. Cargá el TC manual del edificio.`, "warning");
            continue;
          }
        }
        
        // Si hay un criterio pero no se encuentra, o no tiene field, usar criterio del edificio
        if ((criteriaId && !criteria) || !field) {
          gastosSinCriterio.push(expense.description || expense.id);

          // Usar criterio "all" con el método de distribución del edificio
          normalizedField = "all";
          field = "all";
        }
        

        let applicableApartments = [];
        let totalUnits = 0;

        if (normalizedField === "all") {
          // ALL: Dividir entre todos (OWNER y TENANT de cada apartamento)
          // Si NO tiene tenant (NULL, vacío, o campo inexistente), cobrar toda la parte al owner
          // Función helper para verificar si hay tenant válido
          const hasTenant = (apt) => {
            return apt.tenant !== null && 
                   apt.tenant !== undefined && 
                   apt.tenant !== "" && 
                   String(apt.tenant).trim() !== "";
          };
          
          applicableApartments = apartments.map(apt => ({
            ...apt,
            ownerAmount: 0,
            tenantAmount: 0,
            gcAmount: 0,
            ownerOnlyAmount: 0,
            m2Unidad: Number(apt.m2) || 0,
            alicuotaUnidad: Number(apt.alicuota) || 0,
            hasTenant: hasTenant(apt), // Guardar el resultado para usar después
          }));
          // Una parte por UNIDAD (no por persona): assignAmount("all") cobra la
          // parte al OCUPANTE (inquilino, o propietario si no hay). Contar personas
          // hacía baseAmount = gasto/personas → con inquilinos distributed < gasto y
          // el faltante caía entero en una unidad al azar (corrección de centavos).
          // Igual criterio que tenant/owner (una parte por unidad).
          totalUnits = applicableApartments.length;
        } else if (normalizedField === "tenant") {
          // TENANT: Dividir entre todos los TENANT del edificio
          // Si un apartamento NO tiene tenant (NULL, vacío, o campo inexistente), se le cobra al propietario
          // Función helper para verificar si hay tenant válido
          const hasTenant = (apt) => {
            return apt.tenant !== null && 
                   apt.tenant !== undefined && 
                   apt.tenant !== "" && 
                   String(apt.tenant).trim() !== "";
          };
          
          // Contar: tenants reales + (sin tenant → lo asume el propietario, o el
          // sintético unit_<id> si la unidad todavía no tiene propietario). TODA
          // unidad entra al reparto: nunca se cae del divisor por no tener owner.
          const apartmentsWithTenant = apartments.filter(apt => hasTenant(apt));
          const apartmentsWithoutTenant = apartments.filter(apt => !hasTenant(apt));

          // Total de unidades: tenants reales + unidades sin tenant (una por unidad)
          totalUnits = apartmentsWithTenant.length + apartmentsWithoutTenant.length;

          // Todas las unidades son aplicables (sin tenant → paga el propietario/sintético)
          applicableApartments = apartments.map(apt => ({
            ...apt,
            ownerAmount: 0,
            tenantAmount: 0,
            gcAmount: 0,
            ownerOnlyAmount: 0,
            m2Unidad: Number(apt.m2) || 0,
            alicuotaUnidad: Number(apt.alicuota) || 0,
            hasTenant: hasTenant(apt), // Guardar el resultado para usar después
          }));
        } else if (normalizedField === "owner") {
          // OWNER: paga el propietario. TODA unidad entra al reparto: si aún no
          // tiene propietario, su parte se acumula bajo el sintético unit_<id>
          // (upsertOwnerProrated → legacyOwnerHolder) y la hereda el próximo
          // propietario. Nunca se excluye (evita que los demás absorban su parte).
          applicableApartments = apartments.map(apt => ({
            ...apt,
            ownerAmount: 0,
            tenantAmount: 0,
            gcAmount: 0,
            ownerOnlyAmount: 0,
            m2Unidad: Number(apt.m2) || 0,
            alicuotaUnidad: Number(apt.alicuota) || 0,
          }));
          totalUnits = apartments.length;
        } else if (field) {
          // Campo específico: buscar por field en apartments
          // Los campos pueden ser "Si"/"No" (cadena) o true/false (boolean)
          // Verificar ambos casos para mayor compatibilidad
          applicableApartments = apartments.filter(apt => {
            const value = apt[field];
            return value === true || value === "Si" || value === "si" || value === "SI";
          }).map(apt => ({
            ...apt,
            ownerAmount: 0,
            tenantAmount: 0,
            gcAmount: 0,
            ownerOnlyAmount: 0,
            m2Unidad: Number(apt.m2) || 0,
            alicuotaUnidad: Number(apt.alicuota) || 0,
          }));
          totalUnits = applicableApartments.length;
        } else {
          // Sin criterio definido, saltar este gasto
          continue;
        }

        if (totalUnits === 0) {
          continue;
        }
        

        // Helper: verificar si un apartamento tiene tenant válido
        const checkHasTenant = (apt) => {
          if (apt.hasTenant !== undefined) return apt.hasTenant;
          return apt.tenant !== null && apt.tenant !== undefined && apt.tenant !== "" && String(apt.tenant).trim() !== "";
        };

        // Helper: asignar monto a owner/tenant según criterio
        const assignAmount = (apt, amountForApt, nField) => {
          if (nField === "all") {
            if (checkHasTenant(apt)) {
              apt.tenantAmount += amountForApt;  // Gastos comunes 100% al inquilino (Ley 14.219 Art. 77)
            } else {
              apt.ownerAmount += amountForApt;
            }
          } else if (nField === "tenant") {
            // Con inquilino paga el inquilino; sin inquilino lo asume el propietario
            // (o el sintético unit_<id> si la unidad no tiene propietario todavía).
            // Siempre se asigna la parte → la unidad no se cae del reparto.
            apt.tenantAmount += amountForApt;
          } else if (nField === "owner") {
            apt.ownerAmount += amountForApt;
          } else {
            // Criterio específico: cobra al tenant, sino al owner
            if (checkHasTenant(apt)) {
              apt.tenantAmount += amountForApt;
            } else {
              apt.ownerAmount += amountForApt;
            }
          }

          // Clasificación para PRORACIÓN (independiente del snapshot owner/tenant):
          // - "owner": criterio que paga SIEMPRE el propietario → clase ownerOnly.
          // - resto (all/tenant/específico): lo paga el OCUPANTE → clase gc, que
          //   luego se resuelve día a día (inquilino del día; si no hay, propietario).
          if (nField === "owner") {
            apt.ownerOnlyAmount = (apt.ownerOnlyAmount || 0) + amountForApt;
          } else {
            apt.gcAmount = (apt.gcAmount || 0) + amountForApt;
          }
        };

        // FIN-006: Helper para asignar el centavo perdido a la unidad correcta
        // - Por alicuota/superficie: a la unidad de mayor peso; si hay empate, al azar entre empatadas
        // - Por partes iguales: al azar
        const assignLastPennyCorrection = (apartments, remainingCents, weightField, nField) => {
          if (Math.abs(remainingCents) < 0.005) return;
          let candidates;
          if (weightField) {
            const maxWeight = Math.max(...apartments.map(a => a[weightField] || 0));
            candidates = apartments.filter(a => (a[weightField] || 0) === maxWeight);
          } else {
            candidates = [...apartments];
          }
          const lucky = candidates[Math.floor(Math.random() * candidates.length)];
          if (lucky) assignAmount(lucky, remainingCents, nField);
        };

        // Distribuir segun metodo: alicuotas | superficie | partes_iguales
        if (distMethod === "alicuotas" || distMethod === "superficie") {
          const weightField = distMethod === "alicuotas" ? "alicuotaUnidad" : "m2Unidad";
          let totalWeight = applicableApartments.reduce((sum, apt) => sum + (apt[weightField] || 0), 0);

          if (totalWeight === 0 && distMethod === "superficie") {
            totalWeight = buildingM2;
          }

          if (totalWeight > 0) {
            let distributed = 0;
            for (const apt of applicableApartments) {
              const weight = apt[weightField] || 0;
              const amountForApt = roundMoney(expenseAmount * (weight / totalWeight));
              distributed += amountForApt;
              assignAmount(apt, amountForApt, normalizedField);
            }
            // Correccion del centavo perdido: asignar diferencia a la unidad de mayor peso
            const diff = roundMoney(expenseAmount - distributed);
            assignLastPennyCorrection(applicableApartments, diff, weightField, normalizedField);
          } else {
            let distributed = 0;
            const baseAmount = roundMoney(expenseAmount / totalUnits);
            for (const apt of applicableApartments) {
              distributed += baseAmount;
              assignAmount(apt, baseAmount, normalizedField);
            }
            const diff = roundMoney(expenseAmount - distributed);
            assignLastPennyCorrection(applicableApartments, diff, null, normalizedField);
          }
        } else {
          // Partes iguales
          let distributed = 0;
          const baseAmount = roundMoney(expenseAmount / totalUnits);
          for (const apt of applicableApartments) {
            distributed += baseAmount;
            assignAmount(apt, baseAmount, normalizedField);
          }
          // Correccion del centavo perdido: al azar
          const diff = roundMoney(expenseAmount - distributed);
          assignLastPennyCorrection(applicableApartments, diff, null, normalizedField);
        }

        // Validar que la suma sea correcta
        let totalAsignado = 0;
        for (const apt of applicableApartments) {
          totalAsignado += apt.ownerAmount + apt.tenantAmount;
        }
        const diferencia = Math.abs(totalAsignado - expenseAmount);
        if (diferencia > 0.01) {
          console.warn(`Advertencia: El gasto "${expense.description || expense.id}" de $${expenseAmount} no se dividio correctamente. Total asignado: $${totalAsignado.toFixed(2)}, Diferencia: $${diferencia.toFixed(2)}`);
        }

        // Guardar en expensesapartments
        let aptsAdded = 0;
        for (const apt of applicableApartments) {
          if (apt.ownerAmount > 0 || apt.tenantAmount > 0) {
            expensesApartments.push({
              buildingid: Number(buildingFilter),
              apartment: apt.id,
              month: form.month,
              year: form.year,
              periodStart: cierrePeriodStart,
              periodEnd: cierrePeriodEnd,
              amount: apt.ownerAmount + apt.tenantAmount,
              maturity: form.maturity,
              owner: apt.ownerAmount,
              tenant: apt.tenantAmount,
              // Clases para proración por días (no rompen el doc: owner+tenant
              // siguen siendo el snapshot; gc+ownerOnly = mismo total reclasificado).
              gc: apt.gcAmount || 0,
              ownerOnly: apt.ownerOnlyAmount || 0,
              expenseId: expense.id, // Guardar el ID del gasto que se aplicó
              montlyaccountId: cierreDocId,
              publishedToResidents: false,
            });
            aptsAdded++;
          }
        }
      }
      
      // 6. Guardar todos los expensesapartments en batches atomicos (FIN-002)
      let savedCount = 0;
      const BATCH_LIMIT = 450;
      for (let i = 0; i < expensesApartments.length; i += BATCH_LIMIT) {
        const chunk = expensesApartments.slice(i, i + BATCH_LIMIT);
        const batch = writeBatch(db);
        chunk.forEach(expApt => {
          const ref = doc(collection(db, "expensesapartments"));
          batch.set(ref, expApt);
        });
        try {
          await batch.commit();
          savedCount += chunk.length;
        } catch (err) {
          console.error("Error guardando batch de expensesapartments:", err);
          state = "error";
          errorMessage = err.message;
        }
      }

      // 6.5. Crear payments basados en los montos calculados (ownerAmount y tenantAmount)
      // Agrupar los montos por usuario (tanto tenant como owner) desde expensesApartments
      const monthName = getMonthName(form.month);
      const description = `cierre de mes ${monthName}`;
      
      // Guardar los IDs de los expenses para mostrarlos después
      const expenseIds = expenses.map(exp => exp.id);
      
      // Agrupar montos por (usuario, unidad) desde expensesApartments.
      // Política: la deuda es de la UNIDAD. Si un mismo residente tiene N
      // unidades en el edificio, se generan N buckets (uno por unidad) para
      // que al persistir resulten en N payments separados, cada uno con su
      // apartmentId. Antes se agrupaba solo por userid, lo que combinaba el
      // monto total y duplicaba al filtrar por unidad en "Mi Cuenta".
      const userPayments = {}; // clave: `${userid}__${apartmentId || "none"}`
      const bucketKey = (uid, aptId) => `${uid}__${aptId || "none"}`;
      // Helper para encontrar el "primer" bucket del usuario en este cierre
      // (usado por amenity/multa/interés que NO tienen apartmentId).
      const findFirstBucketForUser = (uid) => {
        const key = Object.keys(userPayments).find(
          (k) => userPayments[k].userid === uid && userPayments[k].apartmentId
        );
        return key || null;
      };

      const upsertUserPayment = (targetUserId, amountToAdd, expenseId, apartmentRef, prorationMeta = null) => {
        if (!targetUserId) return;
        const uid = String(targetUserId).trim();
        if (!uid) return;
        const amount = Number(amountToAdd) || 0;
        if (amount <= 0) return;
        const aptId = apartmentRef?.id || null;
        const aptNumber = apartmentRef?.number || null;
        const key = bucketKey(uid, aptId);
        if (!userPayments[key]) {
          userPayments[key] = {
            userid: uid,
            buildingid: Number(buildingFilter),
            apartmentId: aptId,
            apartmentNumber: aptNumber,
            amount: 0,
            maturity: form.maturity,
            description: description,
            paid: false,
            expenseIds: new Set(),
            month: form.month,
            year: form.year,
          };
        }
        userPayments[key].amount += amount;
        if (expenseId) {
          userPayments[key].expenseIds.add(expenseId);
        }
        // Marcar el bucket como prorrateado y guardar el detalle (días/rol/concepto)
        // para transparencia en la liquidación y "Mi Cuenta".
        if (prorationMeta) {
          userPayments[key].prorated = true;
          if (!userPayments[key].prorationDetail) userPayments[key].prorationDetail = [];
          userPayments[key].prorationDetail.push(prorationMeta);
        }
      };

      // Responsables de cada unidad: ver utils/cierreResponsables.js (tenencias del
      // período; sin tenencias, la foto de apartments o unit_<id> sintético).

      // GC-class (lo paga el OCUPANTE): se reparte por responsabilidad diaria
      // (inquilino del día; si no hay inquilino, el propietario cubre ese tramo).
      // Un solo responsable en todo el período → todo a él, sin proración (si su
      // tenencia está cerrada con fecha, a ese userid aunque apartments ya cambió).
      const upsertGcProrated = (amount, apt, expenseId) => {
        if (!(amount > 0)) return;
        const { prorrateado, partes } = repartoGastoComun({
          amount, apt, tramos: occByApt[String(apt.id)], month: form.month, year: form.year, windowBounds,
        });
        if (!prorrateado) {
          upsertUserPayment(partes[0].userid, amount, expenseId, apt);
          return;
        }
        for (const s of partes) {
          upsertUserPayment(s.userid, s.amount, expenseId, apt, {
            userid: String(s.userid), role: s.role, days: s.days, amount: s.amount, concept: "gastos_comunes",
          });
          recordProration(apt, "gastos_comunes", s);
        }
      };

      // Owner-class (lo paga SIEMPRE el propietario: criterio "owner", fondo de
      // reserva, fondos custom): se reparte entre los PROPIETARIOS del mes por días.
      const upsertOwnerProrated = (amount, apt, expenseId, concept = "gastos_comunes") => {
        if (!(amount > 0)) return;
        const { prorrateado, partes } = repartoPropietario({
          amount, apt, tramos: occByApt[String(apt.id)], month: form.month, year: form.year, windowBounds,
        });
        if (!prorrateado) {
          upsertUserPayment(partes[0].userid, amount, expenseId, apt);
          return;
        }
        for (const s of partes) {
          upsertUserPayment(s.userid, s.amount, expenseId, apt, {
            userid: s.userid, role: "owner", days: s.days, amount: s.amount, concept,
          });
          recordProration(apt, concept, { userid: s.userid, role: "owner", days: s.days, amount: s.amount });
        }
      };

      // Asegura un bucket por (ownerId, apt.id) con la forma de los buckets de
      // fondos (expenseIds como array). Reutilizado por fondo de reserva y custom.
      const ensureUnitOwnerBucket = (apt, ownerId) => {
        const key = bucketKey(ownerId, apt.id);
        if (!userPayments[key]) {
          userPayments[key] = {
            userid: String(ownerId),
            buildingid: Number(buildingFilter),
            apartmentId: apt.id,
            apartmentNumber: apt.number || null,
            amount: 0,
            maturity: form.maturity,
            description: `cierre de mes ${getMonthName(form.month)}`,
            paid: false,
            expenseIds: [],
            month: form.month,
            year: form.year,
          };
        }
        return key;
      };

      // Reparte un monto que paga SIEMPRE el propietario (fondo de reserva / custom)
      // entre los propietarios del mes por días si hubo cambio de propietario, y
      // ejecuta `applyShare(ownerId, share, prorated)` por cada parte. Sin cambio
      // de propietario → una sola llamada con el propietario del período (su
      // tenencia cerrada con fecha; si no, el vigente en apartments).
      // En pesos enteros: el monto de la unidad se redondea y, si se prorratea, cada
      // parte también; la última se lleva la diferencia para que sumen lo de la unidad.
      const addOwnerFundToUnit = (apt, amountRaw, applyShare) => {
        const amount = roundPesos(amountRaw);
        if (!(amount > 0)) return;
        const { prorrateado, partes } = repartoPropietario({
          amount, apt, tramos: occByApt[String(apt.id)], month: form.month, year: form.year, windowBounds,
        });
        if (!prorrateado) {
          applyShare(partes[0].userid, amount, false, null);
          return;
        }
        let asignado = 0;
        partes.forEach((s, i) => {
          const resto = amount - asignado;
          const share = i === partes.length - 1 ? resto : Math.min(roundPesos(s.amount), resto);
          asignado += share;
          if (share > 0) applyShare(s.userid, share, true, { days: s.days });
        });
      };

      // Recorrer expensesApartments y sumar montos por (usuario, unidad).
      // Se usa la reclasificación gc/ownerOnly (no owner/tenant del snapshot):
      // para una unidad SIN transición el resultado es idéntico al anterior.
      for (const expApt of expensesApartments) {
        const apt = apartments.find(a => a.id === expApt.apartment);
        if (!apt) continue;

        // GC-class: ocupante del día (con proración si hubo cambio a mitad de mes).
        if ((expApt.gc || 0) > 0) {
          upsertGcProrated(expApt.gc, apt, expApt.expenseId);
        }
        // Owner-class (criterio "owner"): propietario, prorrateado si cambió.
        if ((expApt.ownerOnly || 0) > 0) {
          upsertOwnerProrated(expApt.ownerOnly, apt, expApt.expenseId, "gastos_comunes");
        }
      }

      // Convertir Sets a Arrays y pasar a pesos enteros: el cierre no reparte
      // centésimos. La parte de cada unidad se redondea PARA ARRIBA (decisión de
      // Fefo: dos unidades con la misma parte pagan lo mismo); lo que sobra, unos
      // pocos pesos, queda en la caja (redondeoGc, se informa en el resumen). Lo que
      // se suma después (amenities, mora, multas, cargos, fondos) también entra en
      // pesos enteros.
      let redondeoGc = 0;
      Object.keys(userPayments).forEach((key) => {
        userPayments[key].expenseIds = Array.from(userPayments[key].expenseIds);
        const enPesos = pesosParaArriba(userPayments[key].amount);
        redondeoGc += enPesos - userPayments[key].amount;
        userPayments[key].amount = enPesos;
      });

      // Alquiler de las unidades del edificio: un pago por inquilino y unidad, con la
      // línea `alquiler` (va antes de amenities/multas/cargos para que los del
      // inquilino caigan en este mismo pago vía findFirstBucketForUser).
      for (const linea of lineasAlquiler) {
        const key = bucketKey(linea.userid, linea.apartmentId);
        if (!userPayments[key]) {
          userPayments[key] = {
            userid: linea.userid,
            buildingid: Number(buildingFilter),
            apartmentId: linea.apartmentId,
            apartmentNumber: linea.apartmentNumber,
            amount: 0,
            maturity: form.maturity,
            description: `Alquiler ${nombrePeriodo(linea.periodo)}`,
            paid: false,
            expenseIds: [],
            amenityChargeIds: [],
            month: form.month,
            year: form.year,
          };
        }
        const b = userPayments[key];
        b.amount += linea.monto;
        b.rentAmount = (b.rentAmount || 0) + linea.monto;
        b.rentFundId = linea.fundId;
        b.rentContractId = linea.contratoId;
        b.rentPeriod = linea.periodo;
        b.concepto = "alquiler";
      }

      // ============================================================
      // Integrar amenity charges pendientes del mes
      // ============================================================
      let amenityChargesIncluded = 0;
      try {
        const amenityChargesQ = query(
          collection(db, "amenitycharges"),
          where("buildingid", "in", tolerantId(buildingFilter))
        );
        const amenityChargesFullSnap = await getDocs(amenityChargesQ);
        const amenityChargesSnap = { docs: amenityChargesFullSnap.docs.filter((d) => {
          const data = d.data();
          if (data.month !== form.month || data.year !== form.year) return false;
          // "pending" (primera pasada) O los que ESTE cierre bloqueó en la primera
          // pasada (recuperación de huérfanos). Si hay pagos vencidos, el cierre hace
          // un return de preview de intereses y re-corre desde cero: sin incluir los
          // ya "locked_for_cierre" de este cierre, los amenities se caían del reparto
          // y quedaban pegados en locked_for_cierre sin cobrarse nunca.
          return data.status === "pending"
            || (data.status === "locked_for_cierre" && data.lockedByCierreId === cierreDocId);
        })};

        for (const chargeDoc of amenityChargesSnap.docs) {
          const charge = chargeDoc.data();
          const chargeUserId = String(charge.userid);
          let chargeAmount = Number(charge.cost) || 0;
          if (chargeAmount <= 0) continue;

          // Conversión UI/UR/USD → UYU al TC del día anterior a la fecha de uso
          const chargeCurrency = (charge.currency || "UYU").toUpperCase();
          if (chargeCurrency === "UI" || chargeCurrency === "UR" || chargeCurrency === "USD") {
            try {
              let rate = await getRateForPreviousDay(charge.date);
              if (!rate) {
                const { getLatestRate } = await import("../../utils/exchangeRates");
                rate = await getLatestRate();
              }
              const conversionRate = chargeCurrency === "UI" ? rate?.ui
                : chargeCurrency === "UR" ? rate?.ur
                : rate?.sell;
              if (conversionRate && conversionRate > 0) {
                const originalCost = chargeAmount;
                chargeAmount = roundPesos(originalCost * conversionRate);
                await updateDoc(doc(db, "amenitycharges", chargeDoc.id), {
                  originalCost,
                  originalCurrency: chargeCurrency,
                  conversionRate,
                  conversionDate: rate?.date || charge.date,
                  cost: chargeAmount,
                  currency: "UYU",
                });
              }
            } catch (convErr) {
              console.error(`Error convirtiendo amenity charge ${chargeDoc.id} (${chargeCurrency}):`, convErr);
            }
          }
          chargeAmount = roundPesos(chargeAmount);

          const description = `cierre de mes ${getMonthName(form.month)}`;

          // Resolver el bucket donde cargar el amenity: si el usuario tiene
          // gastos comunes en alguna unidad en este cierre, lo cargamos en
          // ESE bucket (la primera unidad). Si no tiene ninguna, creamos un
          // bucket sin apartmentId. Esto evita duplicar el cargo entre N
          // unidades del mismo usuario.
          let chargeKey = findFirstBucketForUser(chargeUserId);
          if (!chargeKey) {
            chargeKey = bucketKey(chargeUserId, null);
            if (!userPayments[chargeKey]) {
              userPayments[chargeKey] = {
                userid: chargeUserId,
                buildingid: Number(buildingFilter),
                apartmentId: null,
                apartmentNumber: null,
                amount: 0,
                maturity: form.maturity,
                description: description,
                paid: false,
                expenseIds: [],
                amenityChargeIds: [],
                month: form.month,
                year: form.year,
              };
            }
          }
          const chargeBucket = userPayments[chargeKey];
          if (!chargeBucket.amenityChargeIds) {
            chargeBucket.amenityChargeIds = [];
          }

          chargeBucket.amount += chargeAmount;
          chargeBucket.amount = roundMoney(chargeBucket.amount);
          chargeBucket.amenityChargeIds.push(chargeDoc.id);

          // Trackear a qué fondo va cada cargo de amenity
          if (!chargeBucket.amenityByFund) {
            chargeBucket.amenityByFund = {};
          }
          const amenityFundId = charge.targetFundId || "reserva";
          chargeBucket.amenityByFund[amenityFundId] =
            roundMoney((chargeBucket.amenityByFund[amenityFundId] || 0) + chargeAmount);

          // Guardar detalle individual para desglose en breakdown
          if (!chargeBucket.amenityDetails) {
            chargeBucket.amenityDetails = [];
          }
          chargeBucket.amenityDetails.push({
            amenity: charge.amenity || "Amenity",
            date: charge.date || "",
            turno: charge.turno || "",
            amount: chargeAmount,
            fundId: amenityFundId,
          });

          amenityChargesIncluded++;
        }

        // FIN-029: Marcar amenity charges como locked al crear el cierre (antes de aprobacion)
        for (const chargeDoc of amenityChargesSnap.docs) {
          const charge = chargeDoc.data();
          if ((Number(charge.cost) || 0) <= 0) continue;
          await updateDoc(doc(db, "amenitycharges", chargeDoc.id), {
            status: "locked_for_cierre",
            lockedAt: serverTimestamp(),
            lockedByCierreId: cierreDocId,
          });
        }
      } catch (amenityErr) {
        console.error("Error procesando amenity charges:", amenityErr);
        // Non-blocking: continuar con el cierre aunque falle el lock de amenity charges
      }

      // ============================================================
      // Integrar intereses por mora de pagos vencidos
      // ============================================================
      let interestItems = [];
      // Deudores con mora cuya ficha no se pudo verificar (la búsqueda falló o
      // no la devolvió): su mora queda a su nombre y el resumen final los lista.
      const deudoresSinVerificar = new Set();
      try {
        // Config de mora del edificio (Ley 19.604: tope 12% no capitalizable +
        // indexación opcional por UI / Decreto-Ley 14.500).
        const interestRate = Math.min(Number(building?.interestRate) || 0, 12);
        const interestMethod = building?.interestMethod || "porcentaje";
        const interestFixedAmount = Number(building?.interestFixedAmount) || 0;
        const gracePeriodDays = Number(building?.gracePeriodDays) || 0;
        const uiIndexation = building?.moraIndexationEnabled === true;
        const moraStartDate = building?.moraStartDate ? String(building.moraStartDate) : null;
        const hasInterestConfig = interestMethod === "monto_fijo" ? interestFixedAmount > 0 : interestRate > 0;

        if (hasInterestConfig) {
          if (Array.isArray(approvedInterestItems)) {
            // Usar los items aprobados desde el preview (ya vienen enriquecidos
            // con accrualStart/End y valores de UI).
            interestItems = approvedInterestItems;
          } else {
            // ----- Helpers de fecha (ISO local "YYYY-MM-DD") -----
            const isoOf = (d) => {
              if (!d) return null;
              const x = d instanceof Date ? d : new Date(d);
              if (isNaN(x.getTime())) return null;
              return `${x.getFullYear()}-${String(x.getMonth() + 1).padStart(2, "0")}-${String(x.getDate()).padStart(2, "0")}`;
            };
            const addDaysIso = (iso, n) => {
              const [y, m, d] = String(iso).split("-").map(Number);
              const dt = new Date(y, (m || 1) - 1, d || 1);
              dt.setDate(dt.getDate() + (Number(n) || 0));
              return isoOf(dt);
            };
            const maxIso = (a, b) => (!a ? b : !b ? a : a > b ? a : b);
            const minIso = (a, b) => (!a ? b : !b ? a : a < b ? a : b);
            const calcDateIso = form.maturity || isoOf(new Date());

            // Traer TODOS los pagos del edificio (no solo impagos): también
            // cobramos mora a quien pagó TARDE entre cierres.
            const allPaysSnap = await getDocs(
              query(collection(db, "payments"), where("buildingid", "in", tolerantId(buildingFilter)))
            );
            const allPays = allPaysSnap.docs.map((d) => ({ id: d.id, ...d.data() }));

            // "Hasta cuándo ya se cobró mora" se DERIVA de los pagos de interés
            // existentes (interestBreakdown.accrualEnd por paymentId), NO de una
            // marca persistente. Así, al borrar un cierre (que borra sus pagos de
            // interés), la mora se recalcula sola — sin estado huérfano.
            const chargedThroughByPaymentId = {};
            allPays.forEach((p) => {
              (p.interestBreakdown || []).forEach((ib) => {
                if (ib && ib.paymentId && ib.accrualEnd) {
                  const cur = chargedThroughByPaymentId[ib.paymentId];
                  if (!cur || String(ib.accrualEnd) > cur) {
                    chargedThroughByPaymentId[ib.paymentId] = String(ib.accrualEnd);
                  }
                }
              });
            });

            const candidates = allPays.filter((p) => {
              if (p.month === form.month && p.year === form.year) return false; // excluir período del cierre
              if (!p.maturity) return false;
              // Excluir PAGOS A PROVEEDORES (gastos del edificio): se registran en la
              // colección `payments` con `expensesid` pero SIN unidad/dueño ni `paid`
              // (es la plata que SALIÓ, no una cuota que un residente debe). No son
              // deudas de residentes → no deben generar mora. Antes se colaban y el
              // cierre mostraba "Servicio de energía eléctrica", "Jardinero", etc.
              // bajo una unidad cualquiera, cobrándoles mora falsa.
              if (p.expensesid != null) return false;
              // Belt-and-suspenders: un candidato a mora tiene que poder atribuirse a
              // un residente (userid) o unidad (apartmentId); si no, no es una deuda.
              if (!p.userid && !p.apartmentId) return false;
              // Excluir saldos de apertura / migración: aperturas sin vencimiento
              // real, no acumulan mora automáticamente.
              if (p.isMigration === true || /saldo\s*inicial/i.test(p.description || "")) return false;
              // El alquiler de una unidad del edificio nunca genera mora (decisión de
              // Fefo), ni lo que se cobró en ese mismo pago; tampoco las deudas de una
              // unidad del edificio (portería), que no es de ningún residente.
              if (esPagoDeAlquiler(p)) return false;
              if (p.apartmentId && apartmentsAll.some((a) => a.id === p.apartmentId && esUnidadDelEdificio(a))) return false;
              if (p.paid !== true) return true; // impago → candidato
              // Pagado: solo si pagó después del vencimiento (mora por atraso).
              const pdIso = isoOf(getEffectivePaidDate(p));
              return !!pdIso && pdIso > String(p.maturity);
            });

            // Caché de valores de UI por fecha (evita queries repetidas).
            const uiCache = {};
            const getUi = async (iso) => {
              if (!uiIndexation || !iso) return null;
              if (iso in uiCache) return uiCache[iso];
              let v = null;
              try {
                const r = await getExchangeRate(new Date(iso + "T00:00:00"));
                v = r?.ui || null;
              } catch (e) { /* sin UI → no indexar ese tramo */ }
              uiCache[iso] = v;
              return v;
            };

            // Enriquecer cada candidato con su período de devengo INCREMENTAL
            // (no recobra lo ya cobrado) y los valores de UI de los extremos.
            const enriched = [];
            for (const p of candidates) {
              // Fin de gracia según el modo del edificio (días / fin de mes / día fijo).
              const startBase = computeGraceEnd(String(p.maturity), building);
              const charged = chargedThroughByPaymentId[p.id] || null; // derivado, no persistente
              let accrualStart = maxIso(startBase, charged || startBase);
              if (moraStartDate) accrualStart = maxIso(accrualStart, moraStartDate);
              const paid = p.paid === true;
              const paidIso = paid ? isoOf(getEffectivePaidDate(p)) : null;
              const accrualEnd = paid ? minIso(paidIso, calcDateIso) : calcDateIso;
              if (!accrualEnd || !accrualStart || accrualEnd <= accrualStart) continue; // sin período nuevo
              const uiStart = await getUi(accrualStart);
              const uiEnd = await getUi(accrualEnd);
              enriched.push({
                ...p,
                accrualStartDate: accrualStart,
                accrualEndDate: accrualEnd,
                uiStart,
                uiEnd,
                latePaid: paid,
                paidDateIso: paidIso,
              });
            }

            const calculatedItems = calculateInterestsForOverduePayments(
              enriched,
              interestRate,
              calcDateIso,
              { gracePeriodDays, interestMethod, interestFixedAmount }
            );
            // Propagar flags de origen a los items calculados (para write-back/preview).
            const byId = {};
            enriched.forEach((e) => { byId[e.id] = e; });
            // Concepto al que corresponde la deuda original (gastos comunes,
            // fondo de reserva, etc.), derivado del desglose del pago vencido.
            const conceptoOf = (p) => {
              const bd = p?.breakdown || {};
              const parts = Object.entries(bd)
                .filter(([k, v]) => (Number(v) || 0) > 0 && k !== "intereses" && k !== "reajuste")
                .map(([k]) => friendlyBreakdownName(k, building));
              if (parts.length > 0) return [...new Set(parts)].join(", ");
              if (p?.isMigration || /saldo\s*inicial/i.test(p?.description || "")) return "Saldo inicial";
              return p?.description || "—";
            };
            const itemsWithMora = calculatedItems
              .filter((i) => i.moraTotal > 0)
              .map((i) => {
                const src = byId[i.paymentId] || {};
                // 1) Por apartmentId del pago vencido. 2) Por la unidad VIGENTE
                // del deudor (titular o adicional) → multas/amenities sin unidad
                // quedan bajo la unidad de la persona. 3) Si no es residente
                // vigente → sin unidad ("—").
                const uid = String(i.userid);
                const apt = apartmentsAll.find((a) => a.id === src.apartmentId)
                  || apartments.find((a) =>
                    String(a.owner) === uid || String(a.tenant) === uid ||
                    String(a.ownerSecondary) === uid || String(a.tenantSecondary) === uid);
                const resolvedAptId = src.apartmentId || apt?.id || null;
                const unitNumber = apt?.number || src.apartmentNumber || "—";
                const periodo = src.month && src.year ? `${getMonthName(src.month)} ${src.year}` : (src.description || "—");
                return {
                  ...i,
                  apartmentId: resolvedAptId,
                  apartmentNumber: apt?.number || src.apartmentNumber || null,
                  unitNumber,
                  periodo,
                  concepto: conceptoOf(src),
                  latePaid: !!src.latePaid,
                  paidDateIso: src.paidDateIso || null,
                };
              })
              // Ordenar por unidad (numérico); los sin unidad ("—") van al final.
              .sort((a, b) => {
                const ua = a.unitNumber === "—" ? "￿" : a.unitNumber;
                const ub = b.unitNumber === "—" ? "￿" : b.unitNumber;
                return String(ua).localeCompare(String(ub), "es", { numeric: true });
              });

            if (itemsWithMora.length === 0) {
              interestItems = [];
            } else {
              // Mostrar la vista previa para que el admin apruebe.
              setInterestPreview(itemsWithMora);
              setShowInterestPreview(true);
              setSaving(false);
              return; // Pausar cierre hasta confirmar
            }
          }

          // Fichas de los deudores: las del edificio (leídas antes del candado)
          // y, para los deudores con unidad que no están entre ellas (p. ej. un
          // ocupante saliente sin userbuilding), una búsqueda por userid con el
          // edificio fijado (C10). Con C10, que la ficha no vuelva NO prueba que
          // no exista (accessBids viejo o incompleto: trigger caído, backfill a
          // medias), y si la búsqueda falla tampoco se sabe: en los dos casos su
          // mora NO pasa a otra persona y el resumen lo lista.
          const fichaPorUserid = new Map();
          const anotarFicha = (u) => {
            const k = String(u.userid);
            if (!fichaPorUserid.has(k)) fichaPorUserid.set(k, u);
          };
          fichasCierre.forEach(anotarFicha);
          const deudoresABuscar = [
            ...new Set(
              interestItems
                .filter((i) => i.include !== false && i.apartmentId && i.userid != null && i.userid !== "")
                .map((i) => String(i.userid))
            ),
          ].filter((u) => !fichaPorUserid.has(u) && !u.startsWith("unit_"));
          if (deudoresABuscar.length > 0) {
            try {
              (await fetchUsersByUserIds(deudoresABuscar, buildingFilter)).forEach(anotarFicha);
            } catch (fichaErr) {
              console.error("Cierre: no se pudieron verificar las fichas de los deudores con mora:", fichaErr);
            }
            deudoresABuscar.filter((u) => !fichaPorUserid.has(u)).forEach((u) => deudoresSinVerificar.add(u));
          }

          // Aplicar la mora (reajuste + interés) a cada deudor.
          // Se asocia SIEMPRE al apartmentId del pago vencido → queda bajo la
          // unidad y a nombre del deudor (incluido el ocupante saliente).
          for (const item of interestItems) {
            if (item.include === false) continue; // el admin lo desmarcó en el preview
            const moraTotal = Number(item.moraTotal != null ? item.moraTotal : item.interest) || 0;
            if (moraTotal <= 0) continue;
            // Ojo: String(undefined) es "undefined" (truthy) — el cierre fantasma
            // llegó a crear un pago con userid "undefined" por este agujero.
            if (item.userid == null || item.userid === "") continue;
            let uid = String(item.userid);

            // "La deuda es de la UNIDAD" solo para el sintético unit_<id> (pagos
            // creados cuando la unidad no tenía a quién cobrarle): si hoy hay
            // ocupante, la mora es suya. Un deudor con userid real conserva su
            // fila: el de ficha visible (p. ej. un ocupante saliente) y también
            // el de ficha que no se vio (deudoresSinVerificar: con C10 no se
            // distingue "no existe" de "no se ve"), que el resumen lista.
            const overduePaymentApt = item.apartmentId || null;
            if (overduePaymentApt && uid.startsWith("unit_")) {
              const aptDeMora = apartments.find((a) => a.id === overduePaymentApt);
              if (aptDeMora) uid = String(legacyTenantHolder(aptDeMora));
            }

            // Intentar matchear el bucket que corresponde al apartmentId del
            // payment vencido (si el overdue tiene apartmentId).
            let interestKey = overduePaymentApt
              ? bucketKey(uid, overduePaymentApt)
              : null;
            if (interestKey && !userPayments[interestKey]) {
              // No hay bucket de ese apartment todavía: lo creamos
              // (el residente puede haberse mudado / cambiado de unidad).
              const apt = apartmentsAll.find((a) => a.id === overduePaymentApt);
              userPayments[interestKey] = {
                userid: uid,
                buildingid: Number(buildingFilter),
                apartmentId: overduePaymentApt,
                apartmentNumber: apt?.number || null,
                amount: 0,
                maturity: form.maturity,
                description: `cierre de mes ${getMonthName(form.month)}`,
                paid: false,
                expenseIds: [],
                amenityChargeIds: [],
                month: form.month,
                year: form.year,
              };
            }
            if (!interestKey) {
              interestKey = findFirstBucketForUser(uid) || bucketKey(uid, null);
              if (!userPayments[interestKey]) {
                userPayments[interestKey] = {
                  userid: uid,
                  buildingid: Number(buildingFilter),
                  apartmentId: null,
                  apartmentNumber: null,
                  amount: 0,
                  maturity: form.maturity,
                  description: `cierre de mes ${getMonthName(form.month)}`,
                  paid: false,
                  expenseIds: [],
                  amenityChargeIds: [],
                  month: form.month,
                  year: form.year,
                };
              }
            }
            const interestBucket = userPayments[interestKey];
            interestBucket.amount += moraTotal;
            interestBucket.amount = roundMoney(interestBucket.amount);
            interestBucket.reajusteAmount = roundMoney((interestBucket.reajusteAmount || 0) + (Number(item.reajuste) || 0));
            if (!interestBucket.interestBreakdown) {
              interestBucket.interestBreakdown = [];
            }
            // null-coalescing en TODOS los campos: Firestore rechaza `undefined`
            // (pagos de migración/multas/amenities pueden no traer month/year/etc.).
            interestBucket.interestBreakdown.push({
              paymentId: item.paymentId || null,
              originalDebt: Number(item.originalDebt) || 0,
              daysOverdue: item.daysOverdue ?? null,
              effectiveDays: item.effectiveDays ?? null,
              interest: Number(item.interest) || 0,
              reajuste: Number(item.reajuste) || 0,
              factorUI: item.factorUI ?? 1,
              uiStart: item.uiStart ?? null,
              uiEnd: item.uiEnd ?? null,
              accrualStart: item.accrualStartDate || null,
              accrualEnd: item.accrualEndDate || null,
              latePaid: !!item.latePaid,
              rate: item.rate ?? null,
              month: item.month ?? null,
              year: item.year ?? null,
            });
          }
        }
      } catch (intErr) {
        console.error("Error calculando intereses:", intErr);
      }

      // ============================================================
      // Integrar multas activas
      // ============================================================
      let multasIncluded = 0;
      try {
        const multasQ = query(
          collection(db, "multas"),
          where("buildingid", "in", tolerantId(buildingFilter))
        );
        const multasSnap = await getDocs(multasQ);

        // Auto-recuperación: multas que quedaron "incluida_en_cierre" apuntando a
        // un cierre que YA NO EXISTE (porque ese cierre falló a mitad y se borró
        // sin revertirlas) → se reclaman como "activa" para que vuelvan a cobrarse.
        const orphanedMultas = [];
        const cierreExistsCache = {};
        for (const d of multasSnap.docs) {
          const m = d.data();
          if (m.status !== "incluida_en_cierre") continue;
          const cid = m.includedInCierreId;
          if (!cid) { orphanedMultas.push(d); continue; }
          if (!(cid in cierreExistsCache)) {
            try {
              cierreExistsCache[cid] = (await getDoc(doc(db, "montlyaccount", String(cid)))).exists();
            } catch (e) { cierreExistsCache[cid] = true; /* ante la duda, no reclamar */ }
          }
          if (!cierreExistsCache[cid]) orphanedMultas.push(d);
        }
        for (const d of orphanedMultas) {
          try { await updateDoc(doc(db, "multas", d.id), { status: "activa", includedInCierreId: null }); } catch (e) { /* no bloquea */ }
        }

        const multasActivas = multasSnap.docs.filter(
          (d) => d.data().status === "activa" || orphanedMultas.some((o) => o.id === d.id)
        );

        for (const multaDoc of multasActivas) {
          const multa = multaDoc.data();
          const multaUserId = String(multa.userid);
          let multaAmount = Number(multa.amount) || 0;
          if (multaAmount <= 0) continue;

          // Conversión UI/UR/USD → UYU
          const multaCurrency = (multa.currency || "UYU").toUpperCase();
          if (multaCurrency === "UI" || multaCurrency === "UR" || multaCurrency === "USD") {
            try {
              const multaDateStr = multa.createdAt?.toDate?.()
                ? multa.createdAt.toDate().toISOString().split("T")[0]
                : new Date().toISOString().split("T")[0];
              let rate = await getRateForPreviousDay(multaDateStr);
              // Fallback: usar tasa más reciente si no hay del día anterior
              if (!rate) {
                const { getLatestRate } = await import("../../utils/exchangeRates");
                rate = await getLatestRate();
              }
              const conversionRate = multaCurrency === "UI" ? rate?.ui
                : multaCurrency === "UR" ? rate?.ur
                : rate?.sell;
              if (conversionRate && conversionRate > 0) {
                const originalAmount = multaAmount;
                multaAmount = roundPesos(originalAmount * conversionRate);
                await updateDoc(doc(db, "multas", multaDoc.id), {
                  originalAmount,
                  originalCurrency: multaCurrency,
                  conversionRate,
                  conversionDate: rate?.date || multaDateStr,
                  amount: multaAmount,
                  currency: "UYU",
                });
              }
            } catch (convErr) {
              console.error(`Error convirtiendo multa ${multaDoc.id} (${multaCurrency}):`, convErr);
              // Incluir la multa sin convertir en vez de perderla
            }
          }
          multaAmount = roundPesos(multaAmount);

          // Resolver el bucket donde cargar la multa: si la multa tiene
          // apartmentId asociado, usamos ese bucket. Si no, va al primer
          // bucket del usuario en este cierre (típicamente su unidad
          // principal). Como último recurso, creamos bucket sin apartment.
          const multaApartmentId = multa.apartmentId || null;
          let multaKey = multaApartmentId
            ? bucketKey(multaUserId, multaApartmentId)
            : findFirstBucketForUser(multaUserId);
          if (!multaKey) {
            multaKey = bucketKey(multaUserId, null);
          }
          if (!userPayments[multaKey]) {
            const aptForMulta = multaApartmentId
              ? apartmentsAll.find((a) => a.id === multaApartmentId)
              : null;
            userPayments[multaKey] = {
              userid: multaUserId,
              buildingid: Number(buildingFilter),
              apartmentId: multaApartmentId,
              apartmentNumber: aptForMulta?.number || null,
              amount: 0,
              maturity: form.maturity,
              description: `cierre de mes ${getMonthName(form.month)}`,
              paid: false,
              expenseIds: [],
              amenityChargeIds: [],
              month: form.month,
              year: form.year,
            };
          }
          const multaBucket = userPayments[multaKey];
          multaBucket.amount += multaAmount;
          multaBucket.amount = roundMoney(multaBucket.amount);
          if (!multaBucket.multaAmount) {
            multaBucket.multaAmount = 0;
          }
          if (!multaBucket.multaIds) {
            multaBucket.multaIds = [];
          }
          if (!multaBucket.multaByFund) {
            multaBucket.multaByFund = {};
          }
          multaBucket.multaAmount += multaAmount;
          multaBucket.multaIds.push(multaDoc.id);
          // Distribuir monto de multa al fondo destino
          const fundId = multa.targetFundId || (multa.feedsReserveFund ? "reserva" : "reserva");
          multaBucket.multaByFund[fundId] = (multaBucket.multaByFund[fundId] || 0) + multaAmount;

          // Guardar detalle individual para desglose
          if (!multaBucket.multaDetails) {
            multaBucket.multaDetails = [];
          }
          multaBucket.multaDetails.push({
            reason: multa.reason || "Multa",
            amount: multaAmount,
            fundId,
          });

          multasIncluded++;
        }

        // Mark multas as included (after all processing succeeds)
        for (const multaDoc of multasActivas) {
          await updateDoc(doc(db, "multas", multaDoc.id), {
            status: "incluida_en_cierre",
            includedInCierreId: cierreDocId,
          });
        }
      } catch (multaErr) {
        console.error("Error procesando multas:", multaErr);
      }

      // ============================================================
      // Integrar cargos puntuales por unidad (apartmentcharges) — tipo multa
      // ============================================================
      try {
        const chargesQ = query(
          collection(db, "apartmentcharges"),
          where("buildingid", "in", tolerantId(buildingFilter))
        );
        const chargesSnap = await getDocs(chargesQ);

        // Auto-recuperación de huérfanos (mismo criterio que multas).
        const orphanedCharges = [];
        const chgCierreCache = {};
        for (const d of chargesSnap.docs) {
          const c = d.data();
          if (c.status !== "incluida_en_cierre") continue;
          const cid = c.includedInCierreId;
          if (!cid) { orphanedCharges.push(d); continue; }
          if (!(cid in chgCierreCache)) {
            try { chgCierreCache[cid] = (await getDoc(doc(db, "montlyaccount", String(cid)))).exists(); }
            catch (e) { chgCierreCache[cid] = true; }
          }
          if (!chgCierreCache[cid]) orphanedCharges.push(d);
        }
        for (const d of orphanedCharges) {
          try { await updateDoc(doc(db, "apartmentcharges", d.id), { status: "activa", includedInCierreId: null }); } catch (e) { /* no bloquea */ }
        }

        const cargosActivos = chargesSnap.docs.filter(
          (d) => d.data().status === "activa" || orphanedCharges.some((o) => o.id === d.id)
        );

        for (const chargeDoc of cargosActivos) {
          const cargo = chargeDoc.data();
          const cargoUserId = String(cargo.userid);
          let cargoAmount = Number(cargo.amount) || 0;
          if (cargoAmount <= 0) continue;

          // Conversión UI/UR/USD → UYU
          const cargoCurrency = (cargo.currency || "UYU").toUpperCase();
          if (cargoCurrency === "UI" || cargoCurrency === "UR" || cargoCurrency === "USD") {
            try {
              const cargoDateStr = cargo.createdAt?.toDate?.()
                ? cargo.createdAt.toDate().toISOString().split("T")[0]
                : new Date().toISOString().split("T")[0];
              let rate = await getRateForPreviousDay(cargoDateStr);
              if (!rate) { const { getLatestRate } = await import("../../utils/exchangeRates"); rate = await getLatestRate(); }
              const conversionRate = cargoCurrency === "UI" ? rate?.ui : cargoCurrency === "UR" ? rate?.ur : rate?.sell;
              if (conversionRate && conversionRate > 0) {
                const originalAmount = cargoAmount;
                cargoAmount = roundPesos(originalAmount * conversionRate);
                await updateDoc(doc(db, "apartmentcharges", chargeDoc.id), {
                  originalAmount, originalCurrency: cargoCurrency, conversionRate,
                  conversionDate: rate?.date || cargoDateStr, amount: cargoAmount, currency: "UYU",
                });
              }
            } catch (convErr) { console.error(`Error convirtiendo cargo ${chargeDoc.id} (${cargoCurrency}):`, convErr); }
          }
          cargoAmount = roundPesos(cargoAmount);

          const cargoApartmentId = cargo.unitId || null;
          let cargoKey = cargoApartmentId ? bucketKey(cargoUserId, cargoApartmentId) : findFirstBucketForUser(cargoUserId);
          if (!cargoKey) cargoKey = bucketKey(cargoUserId, null);
          if (!userPayments[cargoKey]) {
            const aptForCargo = cargoApartmentId ? apartmentsAll.find((a) => a.id === cargoApartmentId) : null;
            userPayments[cargoKey] = {
              userid: cargoUserId, buildingid: Number(buildingFilter),
              apartmentId: cargoApartmentId, apartmentNumber: aptForCargo?.number || null,
              amount: 0, maturity: form.maturity, description: `cierre de mes ${getMonthName(form.month)}`,
              paid: false, expenseIds: [], amenityChargeIds: [], month: form.month, year: form.year,
            };
          }
          const cargoBucket = userPayments[cargoKey];
          cargoBucket.amount = roundMoney((cargoBucket.amount || 0) + cargoAmount);
          if (!cargoBucket.chargeAmount) cargoBucket.chargeAmount = 0;
          if (!cargoBucket.chargeIds) cargoBucket.chargeIds = [];
          if (!cargoBucket.chargeByFund) cargoBucket.chargeByFund = {};
          if (!cargoBucket.chargeDetails) cargoBucket.chargeDetails = [];
          cargoBucket.chargeAmount += cargoAmount;
          cargoBucket.chargeIds.push(chargeDoc.id);
          const cargoFundId = cargo.targetFundId || "caja";
          cargoBucket.chargeByFund[cargoFundId] = (cargoBucket.chargeByFund[cargoFundId] || 0) + cargoAmount;
          cargoBucket.chargeDetails.push({ concept: cargo.concept || "Cargo", amount: cargoAmount, fundId: cargoFundId });
        }

        for (const chargeDoc of cargosActivos) {
          await updateDoc(doc(db, "apartmentcharges", chargeDoc.id), {
            status: "incluida_en_cierre", includedInCierreId: cierreDocId,
          });
        }
      } catch (cargoErr) {
        console.error("Error procesando cargos puntuales:", cargoErr);
      }

      // ============================================================
      // Calcular fondo de reserva por usuario
      // ============================================================
      try {
        const reserveMethod = building?.reserveFundMethod || null;
        if (reserveMethod) {
          // Base = gastos comunes repartidos del mes (excluye lo que paga un fondo).
          const totalGastosComunes = expenses.reduce(
            (sum, exp) => (isCommonExpense(exp) ? sum + (Number(exp.amount) || 0) : sum), 0
          );

          const reservePct = Number(building.reserveFundPercentage) || 0;
          const reserveFixed = Number(building.reserveFundFixedAmount) || 0;

          // Fondo de reserva: se cobra a TODAS las unidades, al propietario.
          // Si la unidad no tiene propietario, se carga con ID sintético unit_XXX.
          let totalReserve = 0;
          if (reserveMethod === "percentage" && reservePct > 0) {
            totalReserve = totalGastosComunes * reservePct / 100;
          } else if (reserveMethod === "fixed" && reserveFixed > 0) {
            totalReserve = reserveFixed;
          }

          if (totalReserve > 0 && apartments.length > 0) {
            // Aplica la parte de fondo de reserva de una unidad al/los propietario(s)
            // del mes (prorrateado por días si cambió el propietario — decisión #2).
            const addReserveToUnit = (apt, reserveForUnit) => {
              addOwnerFundToUnit(apt, reserveForUnit, (ownerId, share, prorated, meta) => {
                const key = ensureUnitOwnerBucket(apt, ownerId);
                userPayments[key].amount = roundMoney(userPayments[key].amount + share);
                userPayments[key].fondoReservaAmount = roundMoney((userPayments[key].fondoReservaAmount || 0) + share);
                if (prorated) {
                  userPayments[key].prorated = true;
                  if (!userPayments[key].prorationDetail) userPayments[key].prorationDetail = [];
                  userPayments[key].prorationDetail.push({ userid: String(ownerId), role: "owner", days: meta?.days, amount: share, concept: "fondo_reserva" });
                  recordProration(apt, "fondo_reserva", { userid: String(ownerId), role: "owner", days: meta?.days, amount: share });
                }
              });
            };

            // Asegurar bucket por cada unidad (propietario vigente)
            for (const apt of apartments) {
              ensureUnitOwnerBucket(apt, legacyOwnerHolder(apt));
            }

            if (buildingDistMethod === "alicuotas" || buildingDistMethod === "superficie") {
              const weightField = buildingDistMethod === "alicuotas" ? "alicuota" : "m2";
              const unitWeights = apartments.map(apt => ({
                apt,
                weight: Number(apt[weightField]) || 0,
              }));
              const totalWeight = unitWeights.reduce((sum, u) => sum + u.weight, 0);

              if (totalWeight > 0) {
                // Cada unidad, su parte redondeada para arriba (como los gastos
                // comunes); lo que sobra queda en el fondo. Antes la última unidad
                // cargaba la diferencia.
                unitWeights.forEach(({ apt, weight }) => addReserveToUnit(apt, pesosParaArriba(totalReserve * (weight / totalWeight))));
              } else {
                const porUnidad = pesosParaArriba(totalReserve / apartments.length);
                apartments.forEach((apt) => addReserveToUnit(apt, porUnidad));
              }
            } else {
              const porUnidad = pesosParaArriba(totalReserve / apartments.length);
              apartments.forEach((apt) => addReserveToUnit(apt, porUnidad));
            }
          }
        }
      } catch (reserveErr) {
        console.error("Error calculando fondo de reserva:", reserveErr);
      }

      // ============================================================
      // Calcular fondos personalizados por usuario (FIN-019)
      // ============================================================
      try {
        const activeCustomFunds = (building?.customFunds || []).filter(f => f.active !== false);
        if (activeCustomFunds.length > 0 && apartments.length > 0) {
          // Reutilizar totalGastosComunes ya calculado en el bloque de reserva
          const totalGastosComunes = expenses.reduce(
            (sum, exp) => (isCommonExpense(exp) ? sum + (Number(exp.amount) || 0) : sum), 0
          );

          for (const fund of activeCustomFunds) {
            let totalFund = 0;
            if (fund.method === "percentage" && Number(fund.percentage) > 0) {
              totalFund = totalGastosComunes * Number(fund.percentage) / 100;
            } else if (fund.method === "fixed" && Number(fund.fixedAmount) > 0) {
              totalFund = Number(fund.fixedAmount);
            }

            if (totalFund <= 0) continue;

            // Aplica la parte de un fondo custom de una unidad al/los propietario(s)
            // del mes (prorrateado por días si cambió el propietario — decisión #2).
            const addFundToUnit = (apt, amountForUnit) => {
              addOwnerFundToUnit(apt, amountForUnit, (ownerId, share, prorated, meta) => {
                const key = ensureUnitOwnerBucket(apt, ownerId);
                userPayments[key].amount = roundMoney(userPayments[key].amount + share);
                if (!userPayments[key].customFundAmounts) userPayments[key].customFundAmounts = {};
                userPayments[key].customFundAmounts[fund.id] = roundMoney((userPayments[key].customFundAmounts[fund.id] || 0) + share);
                if (prorated) {
                  userPayments[key].prorated = true;
                  if (!userPayments[key].prorationDetail) userPayments[key].prorationDetail = [];
                  userPayments[key].prorationDetail.push({ userid: String(ownerId), role: "owner", days: meta?.days, amount: share, concept: `fondo_${fund.id}` });
                  recordProration(apt, `fondo_${fund.id}`, { userid: String(ownerId), role: "owner", days: meta?.days, amount: share });
                }
              });
            };

            for (const apt of apartments) {
              ensureUnitOwnerBucket(apt, legacyOwnerHolder(apt));
            }

            if (buildingDistMethod === "alicuotas" || buildingDistMethod === "superficie") {
              const weightField = buildingDistMethod === "alicuotas" ? "alicuota" : "m2";
              const unitWeights = apartments.map(apt => ({
                apt,
                weight: Number(apt[weightField]) || 0,
              }));
              const totalWeight = unitWeights.reduce((sum, u) => sum + u.weight, 0);

              if (totalWeight > 0) {
                // Para arriba por unidad, como el fondo de reserva.
                unitWeights.forEach(({ apt, weight }) => addFundToUnit(apt, pesosParaArriba(totalFund * (weight / totalWeight))));
              } else {
                // Fallback: equitativo entre todas las unidades
                const porUnidad = pesosParaArriba(totalFund / apartments.length);
                apartments.forEach((apt) => addFundToUnit(apt, porUnidad));
              }
            } else {
              // Partes iguales
              const porUnidad = pesosParaArriba(totalFund / apartments.length);
              apartments.forEach((apt) => addFundToUnit(apt, porUnidad));
            }
          }
        }
      } catch (customFundErr) {
        console.error("Error calculando fondos personalizados:", customFundErr);
      }

      // Crear payments para cada usuario
      let paymentsCreated = 0;
      let paymentsErrors = [];
      
      // Crear payments en batches atomicos (FIN-002)
      const paymentDataList = [];
      for (const userId in userPayments) {
        const payment = userPayments[userId];
        // Pesos enteros: cada componente ya entra redondeado; esto es la garantía
        // final de que el pago y cada línea de su desglose no llevan centésimos.
        // gastos_comunes es lo que resta, así el desglose suma exacto el total.
        payment.amount = roundPesos(payment.amount);
        if (payment.amount > 0) {
          const interestTotal = roundPesos((payment.interestBreakdown || []).reduce((s, i) => s + (i.interest || 0), 0));
          // Reajuste por indexación UI (Decreto-Ley 14.500), separado del interés.
          const reajusteTotal = roundPesos((payment.interestBreakdown || []).reduce((s, i) => s + (i.reajuste || 0), 0));
          const multaTotal = roundPesos(payment.multaAmount || 0);
          const chargeTotal = roundPesos(payment.chargeAmount || 0);
          const fondoReserva = roundPesos(payment.fondoReservaAmount || 0);
          // Sumar fondos personalizados para restarlos del cálculo de gastos comunes
          const customFundTotal = Object.values(payment.customFundAmounts || {}).reduce((s, v) => s + roundPesos(v), 0);
          // Restar todos los amenity charges (ahora van desglosados en el breakdown)
          const amenityTotalAll = Object.values(payment.amenityByFund || {}).reduce((s, v) => s + roundPesos(v), 0);
          // Alquiler de una unidad del edificio (ingreso del fondo del contrato).
          const rentTotal = roundPesos(payment.rentAmount || 0);
          const gastosComunes = payment.amount - interestTotal - reajusteTotal - multaTotal - chargeTotal - fondoReserva - customFundTotal - amenityTotalAll - rentTotal;

          const multaByFund = payment.multaByFund || {};
          const chargeByFund = payment.chargeByFund || {};
          const amenityByFund = payment.amenityByFund || {};

          // Construir breakdown de fondos custom (solo fondos base, sin multas ni amenities que van desglosados)
          const customBreakdown = {};
          Object.entries(payment.customFundAmounts || {}).forEach(([fid, amt]) => {
            customBreakdown[fundBreakdownKey(fid)] = roundPesos(amt);
          });

          // Construir líneas desglosadas de multas (userid en key para unicidad en totales)
          const multaBreakdown = {};
          (payment.multaDetails || []).forEach((m, i) => {
            const key = `multa__${(m.reason || "Multa").replace(/\s+/g, "_")}__${payment.userid}_${i}`;
            multaBreakdown[key] = roundPesos(m.amount);
          });

          // Construir líneas desglosadas de cargos puntuales (userid en key para unicidad)
          const chargeBreakdown = {};
          (payment.chargeDetails || []).forEach((c, i) => {
            const key = `cargo__${(c.concept || "Cargo").replace(/\s+/g, "_")}__${payment.userid}_${i}`;
            chargeBreakdown[key] = roundPesos(c.amount);
          });

          // Construir líneas desglosadas de amenities (userid en key para unicidad en totales)
          const amenityBreakdown = {};
          (payment.amenityDetails || []).forEach((a, i) => {
            const key = `amenity__${(a.amenity || "Amenity").replace(/\s+/g, "_")}__${a.date || ""}__${payment.userid}_${i}`;
            amenityBreakdown[key] = roundPesos(a.amount);
          });

          paymentDataList.push({
            userid: payment.userid,
            buildingid: payment.buildingid,
            // Bloque A del fix multi-unidad: cada payment ahora se asocia a
            // UNA unidad específica (apartmentId). Para payments con cargos
            // que no son por unidad (amenity/multa del usuario sin
            // apartmentId), queda null — se muestran en "Mi Cuenta" sin
            // filtro de unidad.
            apartmentId: payment.apartmentId || null,
            apartmentNumber: payment.apartmentNumber || null,
            amount: payment.amount,
            maturity: payment.maturity,
            description: payment.description,
            paid: payment.paid,
            paidAmount: 0,
            remainingAmount: payment.amount,
            expenseIds: payment.expenseIds || [],
            amenityChargeIds: payment.amenityChargeIds || [],
            interestBreakdown: payment.interestBreakdown || [],
            interestAmount: interestTotal,
            reajusteAmount: reajusteTotal,
            fondoReservaAmount: fondoReserva,
            multaAmount: multaTotal,
            multaIds: payment.multaIds || [],
            multaByFund: multaByFund,
            chargeAmount: chargeTotal,
            chargeIds: payment.chargeIds || [],
            chargeByFund: chargeByFund,
            chargeDetails: payment.chargeDetails || [],
            amenityByFund: amenityByFund,
            multaDetails: payment.multaDetails || [],
            amenityDetails: payment.amenityDetails || [],
            customFundAmounts: payment.customFundAmounts || {},
            ...(rentTotal > 0
              ? {
                  concepto: "alquiler",
                  rentAmount: rentTotal,
                  rentFundId: payment.rentFundId,
                  rentContractId: payment.rentContractId,
                  rentPeriod: payment.rentPeriod,
                }
              : {}),
            breakdown: {
              gastos_comunes: gastosComunes,
              fondo_reserva: fondoReserva,
              intereses: interestTotal,
              ...(reajusteTotal > 0 ? { reajuste: reajusteTotal } : {}),
              ...(rentTotal > 0 ? { alquiler: rentTotal } : {}),
              ...multaBreakdown,
              ...chargeBreakdown,
              ...amenityBreakdown,
              ...customBreakdown,
            },
            month: payment.month,
            year: payment.year,
            periodStart: cierrePeriodStart,
            periodEnd: cierrePeriodEnd,
            // Proración por cambio de ocupante a mitad de mes: si este payment es
            // una parte prorrateada, lo marcamos y guardamos el detalle (días/rol/
            // concepto) para mostrarlo en la liquidación y en "Mi Cuenta".
            prorated: payment.prorated || false,
            prorationDetail: payment.prorationDetail || [],
            createdAt: serverTimestamp(),
            montlyaccountId: cierreDocId,
            publishedToResidents: false,
            status: "pendiente",
          });
        }
      }

      for (let i = 0; i < paymentDataList.length; i += BATCH_LIMIT) {
        const chunk = paymentDataList.slice(i, i + BATCH_LIMIT);
        const batch = writeBatch(db);
        chunk.forEach(paymentData => {
          const ref = doc(collection(db, "payments"));
          batch.set(ref, paymentData);
        });
        try {
          await batch.commit();
          paymentsCreated += chunk.length;
        } catch (err) {
          console.error(`Error creando batch de payments:`, err);
          paymentsErrors.push(err.message);
          state = "error";
          errorMessage = err.message;
        }
      }
      if (paymentsErrors.length > 0) {
        console.error(`Errores al crear payments:`, paymentsErrors);
      }

      // 6.5. Aplicar SALDO A FAVOR (sobrepagos) a los períodos pendientes del mismo
      // holder: el crédito cubre automáticamente los cargos del mes → el pago se marca
      // pagado/parcial y se anota lo cubierto en `paidFromCredit`. Al quedar pagado, su
      // desglose recién ahí entra a los fondos (una sola vez); el excedente del sobrepago
      // NO está en los fondos (ratio topeado en fundBalances/fundMovements), así que no
      // se duplica plata. Idempotente: el crédito disponible descuenta lo ya aplicado, y
      // borrar el cierre (que elimina el pago cubierto) restaura el crédito solo.
      try {
        const paysSnap = await getDocs(
          query(collection(db, "payments"), where("buildingid", "in", tolerantId(buildingFilter)))
        );
        const allPays = paysSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
        const creditUpdates = computeCreditApplications(allPays);
        for (let i = 0; i < creditUpdates.length; i += BATCH_LIMIT) {
          const chunk = creditUpdates.slice(i, i + BATCH_LIMIT);
          const cbatch = writeBatch(db);
          chunk.forEach((u) => {
            cbatch.update(doc(db, "payments", u.id), {
              paidFromCredit: u.paidFromCredit,
              paidAmount: u.paidAmount,
              remainingAmount: u.remainingAmount,
              paid: u.paid,
              status: u.status,
              paidFromCreditAt: serverTimestamp(),
              paymentMethod: "saldo_a_favor",
            });
          });
          await cbatch.commit();
        }
        if (creditUpdates.length > 0) {
          console.log(`Saldo a favor aplicado a ${creditUpdates.length} pago(s).`);
        }
      } catch (err) {
        // No bloquea el cierre; el crédito se aplicará en el próximo cierre.
        console.error("Error aplicando saldo a favor a pagos pendientes:", err);
      }

      // Nota: "hasta cuándo se cobró mora" NO se persiste; se deriva de los
      // pagos de interés existentes (interestBreakdown.accrualEnd) en el próximo
      // cierre. Así, borrar un cierre revierte la mora automáticamente.

      // 7. Confirmar estado del cierre ya creado (idempotente). Si OK, queda pendiente de aprobación admin.
      try {
        const cierrePatch = {
          state,
          maturity: form.maturity,
          periodStart: cierrePeriodStart,
          periodEnd: cierrePeriodEnd,
          createdByEmail: auth.currentUser?.email || null,
          createdByUid: auth.currentUser?.uid || null,
        };
        if (state === "ok") {
          cierrePatch.approvalStatus = "pending";
        }
        await updateDoc(cierreDocRef, cierrePatch);
      } catch (err) {
        console.error(`Error creando registro en montlyaccount:`, err);
        state = "error";
        errorMessage = err.message;
      }

      // ============================================================
      // Crear snapshot financiero del mes (buildingfinancials)
      // ============================================================
      if (state === "ok") {
        try {
          const totalPaymentsGenerated = Object.values(userPayments).reduce((s, p) => s + (p.amount || 0), 0);
          const totalExpensesMonth = expenses.reduce((s, e) => s + (Number(e.amount) || 0), 0);
          const totalInterestMonth = interestItems.filter(i => i.include).reduce((s, i) => s + i.interest, 0);
          const totalMultasMonth = Object.values(userPayments).reduce((s, p) => s + (p.multaAmount || 0), 0);
          const totalReservaMonth = Object.values(userPayments).reduce((s, p) => s + (p.fondoReservaAmount || 0), 0);
          // Totales de fondos personalizados para el snapshot
          const totalCustomFundsMonth = {};
          Object.values(userPayments).forEach(p => {
            Object.entries(p.customFundAmounts || {}).forEach(([fid, amt]) => {
              totalCustomFundsMonth[fid] = (totalCustomFundsMonth[fid] || 0) + amt;
            });
          });

          const snapshotId = `${Number(buildingFilter)}_${form.year}_${form.month}`;
          await setDoc(doc(db, "buildingfinancials", snapshotId), {
            buildingid: Number(buildingFilter),
            month: form.month,
            year: form.year,
            totalPaymentsGenerated: roundMoney(totalPaymentsGenerated),
            totalExpenses: roundMoney(totalExpensesMonth),
            totalInterest: roundMoney(totalInterestMonth),
            totalMultas: roundMoney(totalMultasMonth),
            totalReservaFund: roundMoney(totalReservaMonth),
            totalCustomFunds: totalCustomFundsMonth,
            paymentsCount: Object.keys(userPayments).length,
            expensesCount: expenses.length,
            createdAt: serverTimestamp(),
            montlyaccountId: cierreDocId,
            // FIN-025: Snapshot inmutable de gastos y pagos usados en el cierre
            expensesSnapshot: expenses.map(exp => ({
              id: exp.id,
              description: exp.description,
              shortDescription: exp.shortDescription || null,
              amount: Number(exp.amount) || 0,
              supplierid: exp.supplierid,
              invoiceDate: exp.invoiceDate,
              paidDate: exp.paidDate || null,
              criteria: exp.criteria,
              expenseCategory: exp.expenseCategory,
            })),
            paymentsSnapshot: paymentDataList.map(p => ({
              userid: p.userid,
              amount: p.amount,
              breakdown: p.breakdown,
              customFundAmounts: p.customFundAmounts,
            })),
          });
        } catch (snapErr) {
          console.error("Error creando snapshot financiero:", snapErr);
        }
      }

      // Resumen final: los gastos que se repartieron van en TABLA (content del
      // diálogo); lo que pagan los fondos, en una sola línea (no se reparte).
      const gastosRepartidos = expenses.filter((e) => isCommonExpense(e));
      const gastosDeFondos = expenses.filter((e) => !isCommonExpense(e));
      let message = `Cierre de mes generado correctamente. Los importes están pendientes de aprobación en "Revisión de cierres".`;
      if (gastosDeFondos.length) {
        const tf = gastosDeFondos.reduce((s, e) => s + (Number(e.amount) || 0), 0);
        message += `\n\n${gastosDeFondos.length} gasto(s) por $${tf.toLocaleString("es-UY")} los pagó un fondo (reserva u otro): no se repartieron.`;
      }
      if (gastosSinCriterio.length) {
        message += `\n\n${gastosSinCriterio.length} gasto(s) sin criterio se repartieron con el método del edificio (${buildingDistMethod}) entre todas las unidades.`;
      }

      if (interestItems.filter(i => i.include && i.interest > 0).length > 0) {
        const totalInterest = interestItems.filter(i => i.include).reduce((s, i) => s + i.interest, 0);
        message += `\n\nIntereses por mora: $${totalInterest.toLocaleString("es-UY")} (${interestItems.filter(i => i.include).length} pagos vencidos)`;
      }
      if (redondeoGc >= 0.005) {
        message += `\n\nRedondeo: cada unidad paga sus gastos comunes redondeados para arriba; los $${roundMoney(redondeoGc).toLocaleString("es-UY")} de diferencia con el total de gastos quedan en la caja.`;
      }
      if (lineasAlquiler.length > 0) {
        message += `\n\nAlquileres de unidades del edificio: ${lineasAlquiler
          .map((l) => `unidad ${l.apartmentNumber || l.apartmentId} $${l.monto.toLocaleString("es-UY")} (${nombrePeriodo(l.periodo)})`)
          .join("; ")}`;
      }
      if (multasIncluded > 0) {
        message += `\n\nMultas incluidas: ${multasIncluded}`;
      }
      if (amenityChargesIncluded > 0) {
        message += `\nCargos amenities: ${amenityChargesIncluded}`;
      }
      if (deudoresSinVerificar.size > 0) {
        // Unidad y userid de cada uno (sin la ficha no hay nombre), para
        // encontrarlos en la revisión.
        const sinVerificar = [...deudoresSinVerificar].map((uid) => {
          const unidades = [
            ...new Set(
              interestItems
                .filter((i) => i.include !== false && String(i.userid) === uid)
                .map((i) => i.apartmentNumber || i.unitNumber)
                .filter((n) => n && n !== "—")
            ),
          ];
          return unidades.length ? `unidad ${unidades.join(", ")} (usuario ${uid})` : `usuario ${uid}`;
        });
        const MAX_LISTADOS = 10;
        const listado =
          sinVerificar.slice(0, MAX_LISTADOS).join("; ") +
          (sinVerificar.length > MAX_LISTADOS ? `; y ${sinVerificar.length - MAX_LISTADOS} más` : "");
        message += `\n\n⚠️ No se pudieron verificar las fichas de ${deudoresSinVerificar.size} deudor(es) con mora: ${listado}. Su recargo quedó a su nombre, sin pasar al ocupante actual de la unidad. Revisalo en "Revisión de cierres" antes de aprobar; si no reconocés a alguno, avisá a soporte.`;
      }

      if (state === "error") {
        message += `\n\n⚠️ Hubo errores durante el proceso:\n${errorMessage}`;
      }
      
      if (paymentsErrors.length > 0) {
        message += `\n\n⚠️ Errores al crear payments:\n${paymentsErrors.join('\n')}`;
      }
      
      // Gastos cargados por vecinos: marcar sus claims como liquidados por este
      // cierre (bloquea el revert/cambio-de-fondo post-cierre en el panel admin).
      try {
        const claimExpenses = expenses.filter((exp) => exp.source === "resident_claim" && exp.claimId);
        for (const exp of claimExpenses) {
          try {
            await updateDoc(doc(db, "expenseclaims", exp.claimId), {
              consumedByCierreId: cierreDocId,
              consumedByCierreAt: serverTimestamp(),
            });
          } catch (e) {
            console.warn(`No se pudo marcar el claim ${exp.claimId} como liquidado:`, e.message);
          }
        }
      } catch (claimErr) {
        console.warn("Error marcando claims de vecinos como liquidados:", claimErr.message);
      }

      await logAudit("create_cierre", { buildingId: Number(buildingFilter), entityId: cierreDocId, entityType: "montlyaccount", newValue: { month: form.month, year: form.year, state, paymentsCreated, expensesCount: expenses.length } });
      await alertDialog(message, {
        title: "Cierre de mes",
        variant: state === "error" ? "warning" : "info",
        wide: true,
        content: gastosRepartidos.length ? <CierreGastosTabla rows={gastosRepartidos} totalLabel="Total repartido" /> : null,
      });
      
      // Mostrar información adicional en consola
      if (state === "error") {
      }
      setShowNewModal(false);
      setForm(freshForm());
      fetchCierres();
    } catch (error) {
      console.error("Error creando cierre:", error);
      showToast("Error al crear el cierre: " + error.message, "error");
      state = "error";
      try {
        const lockSnap = await getDoc(cierreDocRef);
        if (lockSnap.exists() && lockSnap.data()?.state === "processing") {
          await deleteDoc(cierreDocRef);
        }
        // Revertir multas que se hayan marcado "incluida_en_cierre" en esta
        // corrida fallida, para que no queden huérfanas (vuelven a "activa").
        const orphanQ = query(
          collection(db, "multas"),
          where("buildingid", "in", tolerantId(buildingFilter)),
          where("includedInCierreId", "==", cierreDocId)
        );
        const orphanSnap = await getDocs(orphanQ);
        for (const d of orphanSnap.docs) {
          await updateDoc(doc(db, "multas", d.id), { status: "activa", includedInCierreId: null });
        }
      } catch (lockErr) {
        console.error("Error limpiando cierre fallido:", lockErr);
      }
    } finally {
      setSaving(false);
    }
  };

  // Si no hay edificio seleccionado
  if (!buildingFilter) {
    return (
      <div style={styles.container}>
        <div style={styles.emptyState}>
          <div style={styles.emptyStateIcon}>
            <FaBuilding size={48} color={colors.primary} />
          </div>
          <h2 style={styles.emptyTitle}>Selecciona un Edificio</h2>
          <p style={styles.emptyMessage}>
            Para gestionar el cierre de mes, utiliza el filtro en la barra superior.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      {/* Modal de previsualización de intereses */}
      {showInterestPreview && interestPreview && (
        <div style={{ ...modalOverlay, zIndex: 9999 }}>
          <div style={{ background: colors.white, borderRadius: radius.md, padding: spacing[6], maxWidth: 700, width: "90%", maxHeight: "80vh", overflow: "auto", boxShadow: shadows.modal }}>
            <h3 style={{ margin: "0 0 8px", fontSize: typography.sizes.xl, color: colors.black, fontWeight: 400 }}>Mora por atraso calculada</h3>
            <p style={{ fontSize: typography.sizes.base, color: colors.gray600, marginBottom: spacing[4] }}>Reajuste por UI (Decreto-Ley 14.500) + interés 12% anual no capitalizable (Ley 19.604). Desmarcá lo que no quieras incluir.</p>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: typography.sizes.base }}>
              <thead>
                <tr style={{ background: colors.gray50, textTransform: "uppercase", fontSize: typography.sizes.xs, color: colors.gray600, fontWeight: 400 }}>
                  <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 400 }}>Unidad</th>
                  <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 400 }}>Período</th>
                  <th style={{ padding: "8px 10px", textAlign: "left", fontWeight: 400 }}>Concepto</th>
                  <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 400 }}>Deuda</th>
                  <th style={{ padding: "8px 10px", textAlign: "center", fontWeight: 400 }}>Días</th>
                  <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 400 }}>Reajuste</th>
                  <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 400 }}>Interés</th>
                  <th style={{ padding: "8px 10px", textAlign: "right", fontWeight: 400 }}>Mora total</th>
                  <th style={{ padding: "8px 10px", textAlign: "center", fontWeight: 400 }}>Incluir</th>
                </tr>
              </thead>
              <tbody>
                {interestPreview.map((item, idx) => (
                  <tr key={idx} style={{ borderBottom: `1px solid ${colors.gray200}` }}>
                    <td style={{ padding: "8px 10px" }}>
                      {item.unitNumber}
                      {item.latePaid && (
                        <span style={{ marginLeft: 6, fontSize: typography.sizes.xs, color: colors.warningDark || colors.gray600 }}>(pago tardío)</span>
                      )}
                    </td>
                    <td style={{ padding: "8px 10px", fontSize: typography.sizes.sm, color: colors.gray700 }}>{item.periodo || "—"}</td>
                    <td style={{ padding: "8px 10px", fontSize: typography.sizes.sm, color: colors.gray700 }}>{item.concepto || "—"}</td>
                    <td style={{ padding: "8px 10px", textAlign: "right" }}>${item.originalDebt?.toLocaleString("es-UY")}</td>
                    <td style={{ padding: "8px 10px", textAlign: "center" }}>{item.effectiveDays ?? item.daysOverdue}</td>
                    <td style={{ padding: "8px 10px", textAlign: "right" }}>${(item.reajuste || 0).toLocaleString("es-UY")}</td>
                    <td style={{ padding: "8px 10px", textAlign: "right" }}>${(item.interest || 0).toLocaleString("es-UY")}</td>
                    <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 400 }}>${(item.moraTotal ?? item.interest ?? 0).toLocaleString("es-UY")}</td>
                    <td style={{ padding: "8px 10px", textAlign: "center" }}>
                      <input type="checkbox" checked={item.include} onChange={() => {
                        const updated = [...interestPreview];
                        updated[idx] = { ...updated[idx], include: !updated[idx].include };
                        setInterestPreview(updated);
                      }} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <div style={{ marginTop: spacing[3], fontSize: typography.sizes.base, color: colors.gray700, fontWeight: 400 }}>
              Total mora seleccionada: ${interestPreview.filter(i => i.include).reduce((s, i) => s + (i.moraTotal ?? i.interest ?? 0), 0).toLocaleString("es-UY")}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: spacing[5] }}>
              <button onClick={() => { setShowInterestPreview(false); setInterestPreview(null); }}
                style={{ ...btnSecondary, padding: "8px 16px", fontSize: typography.sizes.base }}>
                Cancelar
              </button>
              <button onClick={() => {
                setShowInterestPreview(false);
                handleCreateCierre(interestPreview);
              }}
                style={{ ...btnPrimary, padding: "8px 16px", fontSize: typography.sizes.base }}>
                Confirmar e incluir intereses
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Header con título y acciones */}
      <ScreenHeader icon={FaCalendarAlt} title="Cierre de Mes" subtitle="Gestión del cierre mensual" />

      {/* Contenido cuando hay edificio seleccionado */}
      {loading ? (
        <LoadingState variant="block" message="Cargando cierres…" />
      ) : (
        <>
          
          {/* Tabla de cierres */}
          <div style={styles.table} data-tutorial="cierre-tabla">
            <div style={styles.thead}>
              <div>Mes</div>
              <div>Año</div>
              <div>Fecha Creación</div>
              <div>Estado</div>
              <div>Publicación</div>
              <div>Acciones</div>
            </div>

            {cierres.length === 0 ? (
              <div style={styles.emptyRow}>
                <FaCalendarAlt size={18} />
                <span>No hay cierres registrados para este edificio.</span>
              </div>
            ) : (
              cierres.map((cierre, idx) => (
                <div key={`${cierre.id}-${idx}`} style={styles.trow}>
                  <div>{getMonthName(cierre.month)}</div>
                  <div>{cierre.year}</div>
                  <div>{formatDate(cierre.createdAt)}</div>
                  <div style={cierre.state === "ok" ? styles.badgeSuccess : styles.badgeError}>
                    {cierre.state === "ok" ? "OK" : "Error"}
                  </div>
                  <div>
                    {cierre.state !== "ok" ? (
                      <span style={{ color: colors.gray600, fontSize: typography.sizes.sm }}>—</span>
                    ) : cierre.approvalStatus === "pending" ? (
                      <span style={styles.badgeWarning}>Pendiente</span>
                    ) : (
                      <span style={styles.badgeSuccess}>Aprobado</span>
                    )}
                  </div>
                  <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                    {cierre.state === "ok" && (
                      <button
                        style={styles.reviewBtn}
                        onClick={() => setSelected("revisioncierres")}
                        title="Revisar cierre"
                        aria-label="Revisar cierre"
                      >
                        <FaEye size={16} />
                      </button>
                    )}
                    <button
                      style={styles.pdfBtn}
                      onClick={() => handleGenerarPDF(cierre)}
                      title="Descargar PDF de liquidacion"
                    >
                      <FaFilePdf size={16} />
                    </button>
                    {cierre.state === "ok" && (
                      <button
                        style={{
                          ...styles.reviewBtn,
                          color: colors.primary,
                          background: colors.primaryLighter,
                        }}
                        onClick={() => handleGenerateNarrative(cierre)}
                        title="Generar comunicado del cierre con IA"
                        aria-label="Generar comunicado con IA"
                        disabled={narrativeLoading}
                      >
                        <FaBullhorn size={14} />
                      </button>
                    )}
                    <button
                      style={styles.deleteBtn}
                      onClick={() => handleDeleteCierre(cierre.id)}
                      title="Eliminar cierre"
                    >
                      <FaTrash size={16} />
                    </button>
                  </div>
                </div>
              ))
            )}
          </div>

          {/* FAB crear cierre */}
          <button
            style={styles.fab}
            title="Nuevo cierre"
            data-tutorial="cierre-nuevo"
            onClick={() => setShowNewModal(true)}
          >
            <FaPlus size={22} />
          </button>
        </>
      )}

      {/* Modal Nuevo Cierre */}
      {showNewModal && (
        <div style={styles.modalOverlay}>
          <div style={styles.modal}>
            <div style={styles.modalHeader}>
              <h2 style={styles.modalTitle}>Nuevo Cierre de Mes</h2>
              <button style={styles.closeBtn} onClick={() => setShowNewModal(false)}>
                ✕
              </button>
            </div>

            <div style={styles.modalBody}>
              <div style={styles.formGroup}>
                <label style={styles.label}>Mes *</label>
                <select
                  style={styles.input}
                  value={form.month}
                  onChange={(e) => setForm({ ...form, month: Number(e.target.value) })}
                >
                  {[
                    { value: 1, label: "Enero" },
                    { value: 2, label: "Febrero" },
                    { value: 3, label: "Marzo" },
                    { value: 4, label: "Abril" },
                    { value: 5, label: "Mayo" },
                    { value: 6, label: "Junio" },
                    { value: 7, label: "Julio" },
                    { value: 8, label: "Agosto" },
                    { value: 9, label: "Septiembre" },
                    { value: 10, label: "Octubre" },
                    { value: 11, label: "Noviembre" },
                    { value: 12, label: "Diciembre" },
                  ].map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Año *</label>
                <input
                  type="number"
                  style={styles.input}
                  value={form.year}
                  onChange={(e) => setForm({ ...form, year: Number(e.target.value) })}
                  min={2020}
                  max={2100}
                />
              </div>

              {/* Período del cierre — fecha de corte configurable. La ventana
                  (Desde/Hasta) define qué movimientos entran y cómo se muestran.
                  Prefilled desde el "día de corte" del edificio; editable. */}
              <div style={{ ...styles.formGroup, background: colors.gray50, padding: 12, borderRadius: radius.sm, border: `1px solid ${colors.gray200}` }}>
                <label style={{ ...styles.label, marginBottom: 8, color: colors.primary }}>Período del cierre</label>
                <div style={{ display: "flex", gap: 10 }}>
                  <div style={{ flex: 1 }}>
                    <label htmlFor="cierre-desde" style={{ ...styles.label, fontSize: 12 }}>Desde</label>
                    <input
                      id="cierre-desde"
                      type="date"
                      style={styles.input}
                      value={form.periodStart}
                      onChange={(e) => setForm({ ...form, periodStart: e.target.value })}
                    />
                  </div>
                  <div style={{ flex: 1 }}>
                    <label htmlFor="cierre-hasta" style={{ ...styles.label, fontSize: 12 }}>Hasta (corte)</label>
                    <input
                      id="cierre-hasta"
                      type="date"
                      style={styles.input}
                      value={form.periodEnd}
                      onChange={(e) => setForm({ ...form, periodEnd: e.target.value })}
                    />
                  </div>
                </div>
                {(() => {
                  const v = getPeriodValidation();
                  return (
                    <>
                      <p style={{ margin: "10px 0 0", fontSize: 13, color: colors.gray700, lineHeight: 1.5 }}>
                        El cierre de <b>{getMonthName(form.month)} {form.year}</b> tomará los movimientos del{" "}
                        <b>{fmtISO(form.periodStart)}</b> al <b>{fmtISO(form.periodEnd)}</b>. Vence el <b>{fmtISO(form.maturity)}</b>.
                      </p>
                      {v.errors.map((msg, i) => (
                        <p key={`e${i}`} style={{ margin: "6px 0 0", fontSize: 12.5, color: colors.error }}>⚠ {msg}</p>
                      ))}
                      {v.warnings.map((msg, i) => (
                        <div key={`w${i}`} style={{ margin: "6px 0 0", fontSize: 12.5, color: colors.warningDark, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                          <span>⚠ {msg}</span>
                          {v.prevEnd && (
                            <button
                              type="button"
                              onClick={() => setForm((prev) => ({ ...prev, periodStart: v.prevEnd }))}
                              style={{ ...btnGhost, padding: "3px 8px", fontSize: 12 }}
                            >
                              Usar {fmtISO(v.prevEnd)}
                            </button>
                          )}
                        </div>
                      ))}
                    </>
                  );
                })()}
              </div>

              <div style={styles.formGroup}>
                <label style={styles.label}>Fecha Vencimiento *</label>
                <input
                  type="date"
                  style={styles.input}
                  value={form.maturity}
                  onChange={(e) => setForm({ ...form, maturity: e.target.value })}
                />
                <p style={{ margin: "4px 0 0", fontSize: 12, color: colors.gray600 }}>
                  Fecha en que los residentes deben pagar (independiente del corte).
                </p>
              </div>

              {/* A8 — Pre-validación con checklist */}
              <div style={{
                marginTop: 16,
                padding: 12,
                background: colors.gray50,
                borderRadius: radius.sm,
                border: `1px solid ${colors.gray200}`,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <strong style={{ fontSize: typography.sizes.sm, color: colors.gray900 }}>
                    Pre-validación del cierre
                  </strong>
                  <button
                    type="button"
                    onClick={runPreValidation}
                    disabled={preValidating}
                    style={{
                      padding: "4px 10px",
                      fontSize: 11,
                      color: colors.primary,
                      background: colors.white,
                      border: `1px solid ${colors.primaryLight}`,
                      borderRadius: 6,
                      cursor: preValidating ? "not-allowed" : "pointer",
                      opacity: preValidating ? 0.6 : 1,
                    }}
                  >
                    {preValidating ? "Validando…" : "Re-chequear"}
                  </button>
                </div>

                {preValidating && !preValidation && (
                  <div style={{ fontSize: 12, color: colors.gray600 }}>
                    Chequeando proveedores recurrentes, gastos impagos y payroll…
                  </div>
                )}

                {preValidation && (
                  <>
                    {preValidation.unavailable ? (
                      <div style={{ fontSize: 12, color: colors.gray600 }}>
                        La pre-validación automática no está disponible en este momento. Podés crear el cierre igual y revisar los gastos manualmente.
                      </div>
                    ) : preValidation.items.length === 0 ? (
                      <div style={{ fontSize: 13, color: colors.success }}>
                        ✓ Todo listo. No detectamos gastos faltantes ni alertas para este mes.
                      </div>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                        {preValidation.items.map((item, idx) => (
                          <div
                            key={idx}
                            style={{
                              padding: 10,
                              borderRadius: 6,
                              background:
                                item.severity === "critical"
                                  ? colors.errorLight
                                  : item.severity === "warning"
                                  ? colors.warningLight
                                  : colors.gray100,
                              borderLeft: `3px solid ${
                                item.severity === "critical"
                                  ? colors.error
                                  : item.severity === "warning"
                                  ? colors.warning
                                  : colors.gray400
                              }`,
                            }}
                          >
                            <div style={{
                              fontSize: 12,
                              color: item.severity === "critical" ? colors.error : colors.gray900,
                              marginBottom: 4,
                            }}>
                              {item.severity === "critical" ? "🚨 " : "⚠️ "}<strong>{item.title}</strong>
                            </div>
                            <div style={{ fontSize: 12, color: colors.gray700, whiteSpace: "pre-line" }}>
                              {item.detail}
                            </div>
                            {item.action_suggested && (
                              <div style={{ fontSize: 11, color: colors.gray600, marginTop: 4, fontStyle: "italic" }}>
                                Acción sugerida: {item.action_suggested}
                              </div>
                            )}
                          </div>
                        ))}

                        {!preValidation.can_close && (
                          <label style={{
                            display: "flex",
                            alignItems: "center",
                            gap: 8,
                            marginTop: 4,
                            padding: 8,
                            background: colors.white,
                            border: `1px solid ${colors.gray300}`,
                            borderRadius: 6,
                            fontSize: 12,
                            color: colors.gray700,
                            cursor: "pointer",
                          }}>
                            <input
                              type="checkbox"
                              checked={forceClose}
                              onChange={(e) => setForceClose(e.target.checked)}
                            />
                            Forzar cierre igual (entiendo que faltan datos críticos)
                          </label>
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>
            </div>

            <div style={styles.modalFooter}>
              <button
                style={styles.btnCancel}
                onClick={() => {
                  setShowNewModal(false);
                  setForm(freshForm());
                }}
                disabled={saving}
              >
                Cancelar
              </button>
              <button
                style={{
                  ...styles.btnSave,
                  opacity: saving ? 0.7 : 1,
                  cursor: saving ? "not-allowed" : "pointer",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  gap: 8,
                }}
                onClick={() => handleCreateCierre()}
                disabled={
                  saving ||
                  !form.maturity ||
                  !form.periodStart ||
                  !form.periodEnd ||
                  form.periodEnd <= form.periodStart ||
                  (preValidation && !preValidation.can_close && !forceClose)
                }
                title={
                  preValidation && !preValidation.can_close && !forceClose
                    ? "Hay alertas críticas sin resolver. Marcá 'Forzar cierre' o resolvé antes."
                    : ""
                }
              >
                {saving ? (
                  <>
                    <FaSpinner className="spin" size={14} />
                    Procesando...
                  </>
                ) : (
                  "Crear Cierre"
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* A5 — Modal de preview del comunicado narrado */}
      {narrativeModal && (
        <div style={modalOverlay}>
          <div style={{
            background: colors.white,
            borderRadius: radius.lg,
            padding: spacing[6],
            maxWidth: 720,
            width: "90%",
            maxHeight: "85vh",
            display: "flex",
            flexDirection: "column",
            boxShadow: shadows.modal,
          }}>
            <div style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: spacing[4],
            }}>
              <h3 style={{ margin: 0, fontSize: typography.sizes.lg, color: colors.primary, fontWeight: 400 }}>
                📋 Comunicado del cierre (preview)
              </h3>
              <button
                onClick={() => setNarrativeModal(null)}
                style={{
                  background: "transparent",
                  border: "none",
                  fontSize: 18,
                  cursor: "pointer",
                  color: colors.gray600,
                }}
                aria-label="Cerrar"
              >
                <FaTimes />
              </button>
            </div>

            {narrativeModal.loading || narrativeLoading ? (
              <LoadingState variant="block" message="Generando comunicado…" />
            ) : (
              <>
                <div style={{
                  flex: 1,
                  overflowY: "auto",
                  padding: spacing[4],
                  background: colors.gray50,
                  borderRadius: radius.md,
                  border: `1px solid ${colors.gray200}`,
                  fontSize: typography.sizes.base,
                  lineHeight: 1.6,
                  color: colors.gray900,
                  whiteSpace: "pre-wrap",
                  fontFamily: "inherit",
                }}>
                  {narrativeModal.body}
                </div>
                <div style={{
                  marginTop: spacing[3],
                  fontSize: 12,
                  color: colors.gray600,
                }}>
                  Revisá antes de publicar. Editá lo que necesites, copiá el texto a Novedades manualmente o usá el botón "Publicar" para crear la novedad ya.
                </div>
                <div style={{
                  display: "flex",
                  gap: 12,
                  marginTop: spacing[4],
                  justifyContent: "flex-end",
                  flexWrap: "wrap",
                }}>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(narrativeModal.body);
                      showToast("Comunicado copiado al portapapeles.", "success");
                    }}
                    style={btnSecondary}
                  >
                    Copiar al portapapeles
                  </button>
                  <button
                    onClick={() => setNarrativeModal(null)}
                    style={btnGhost}
                    disabled={publishingNovedad}
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={handlePublishNarrativeAsNovedad}
                    style={btnPrimary}
                    disabled={publishingNovedad || !narrativeModal.body}
                  >
                    {publishingNovedad ? "Publicando…" : "Publicar como novedad"}
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Estilos migrados a design tokens
const styles = {
  container: {
    ...pageContainer,
  },
  pageHeader: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: spacing[3],
  },
  headerTitleSection: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
  },
  pageTitle: {
    ...pageTitle,
  },
  pageSubtitle: {
    fontSize: typography.sizes.base,
    color: colors.gray600,
    margin: 0,
  },
  table: {
    background: colors.white,
    borderRadius: radius.md,
    boxShadow: shadows.sm,
    overflow: "hidden",
  },
  badgeWarning: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "6px 10px",
    borderRadius: radius.sm,
    background: colors.warningLight,
    color: colors.warningDark,
    fontSize: typography.sizes.sm,
    fontWeight: 400,
  },
  thead: {
    display: "grid",
    gridTemplateColumns: "160px 80px 130px 90px 110px 120px",
    gap: 0,
    padding: "14px 20px",
    background: colors.gray50,
    color: colors.gray600,
    fontWeight: 400,
    fontSize: typography.sizes.sm,
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  trow: {
    display: "grid",
    gridTemplateColumns: "160px 80px 130px 90px 110px 120px",
    padding: "14px 20px",
    borderTop: `1px solid ${colors.gray100}`,
    fontSize: typography.sizes.base,
    alignItems: "center",
    background: colors.white,
    transition: `background ${transitions.normal}`,
  },
  emptyRow: {
    display: "flex",
    alignItems: "center",
    gap: spacing[3],
    padding: spacing[5],
    borderTop: `1px solid ${colors.gray100}`,
    color: colors.gray600,
    background: colors.white,
    fontSize: typography.sizes.base,
  },
  badgeSuccess: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "6px 10px",
    borderRadius: radius.sm,
    background: colors.successLight,
    color: colors.successDark,
    fontSize: typography.sizes.sm,
    fontWeight: 400,
  },
  badgeError: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "6px 10px",
    borderRadius: radius.sm,
    background: colors.errorLight,
    color: colors.errorDark,
    fontSize: typography.sizes.sm,
    fontWeight: 400,
  },
  fab: {
    position: "fixed",
    right: spacing[6],
    bottom: spacing[6],
    width: 52,
    height: 52,
    display: "grid",
    placeItems: "center",
    borderRadius: radius.full,
    background: colors.primary,
    color: colors.white,
    border: "none",
    boxShadow: "0 4px 12px rgba(0,128,255,0.3)",
    cursor: "pointer",
    transition: `all ${transitions.normal}`,
  },
  modalOverlay: {
    ...modalOverlay,
  },
  modal: {
    background: colors.white,
    borderRadius: radius.md,
    width: "100%",
    maxWidth: 480,
    boxShadow: shadows.modal,
    // Nunca más alto que la pantalla: el cuerpo scrollea y el header/footer
    // (botón Crear) quedan siempre visibles en notebooks o pantallas chicas.
    maxHeight: `calc(100vh - ${spacing[4] * 2}px)`,
    display: "flex",
    flexDirection: "column",
    overflow: "hidden",
  },
  modalHeader: {
    flexShrink: 0,
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: `${spacing[5]}px ${spacing[6]}px`,
    borderBottom: `1px solid ${colors.gray100}`,
  },
  modalTitle: {
    margin: 0,
    fontSize: typography.sizes.xl,
    fontWeight: 400,
    color: colors.black,
    fontFamily: typography.fontFamily,
  },
  closeBtn: {
    ...btnGhost,
    fontSize: 18,
  },
  modalBody: {
    padding: spacing[6],
    display: "flex",
    flexDirection: "column",
    gap: spacing[4],
    overflowY: "auto",
    flex: "1 1 auto",
    minHeight: 0,
  },
  formGroup: {
    display: "flex",
    flexDirection: "column",
    gap: 6,
  },
  label: {
    fontSize: typography.sizes.base,
    fontWeight: 400,
    color: colors.gray900,
    fontFamily: typography.fontFamily,
  },
  input: {
    ...inputBase,
  },
  modalFooter: {
    flexShrink: 0,
    display: "flex",
    justifyContent: "flex-end",
    gap: 10,
    padding: `${spacing[4]}px ${spacing[6]}px`,
    borderTop: `1px solid ${colors.gray100}`,
    background: colors.gray50,
    borderRadius: `0 0 ${radius.md}px ${radius.md}px`,
  },
  btnCancel: {
    ...btnSecondary,
    padding: "10px 16px",
  },
  btnSave: {
    ...btnPrimary,
    padding: "10px 20px",
  },
  reviewBtn: {
    ...btnGhost,
    border: `1px solid ${colors.success}`,
    borderRadius: radius.sm,
    padding: "6px 10px",
    color: colors.success,
  },
  pdfBtn: {
    ...btnGhost,
    border: `1px solid ${colors.primary}`,
    borderRadius: radius.sm,
    padding: "6px 10px",
    color: colors.primary,
  },
  deleteBtn: {
    ...btnGhost,
    border: `1px solid ${colors.errorLight}`,
    borderRadius: radius.sm,
    padding: "6px 10px",
    color: colors.errorDark,
  },
  backBtnAlone: {
    ...btnSecondary,
    padding: "10px 16px",
    display: "flex",
    alignItems: "center",
    gap: spacing[2],
  },
  emptyState: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    padding: `${spacing[16]}px ${spacing[8]}px`,
    textAlign: "center",
    background: colors.white,
    borderRadius: radius.md,
    boxShadow: shadows.sm,
    marginTop: spacing[6],
  },
  emptyStateIcon: {
    marginBottom: spacing[5],
    opacity: 0.8,
  },
  emptyTitle: {
    fontSize: typography.sizes.xl,
    fontWeight: 400,
    color: colors.black,
    margin: "0 0 8px 0",
    fontFamily: typography.fontFamily,
  },
  emptyMessage: {
    fontSize: typography.sizes.md,
    color: colors.gray600,
    margin: 0,
    lineHeight: 1.6,
    maxWidth: 320,
  },
};

export default CierreMes;

